import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useAuth } from '@/lib/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { agentsAPI, applicationsAPI, employeesAPI, statsAPI, universitiesAPI, adminNotesAPI, adminStudentsAPI, payoutAPI, coursesAPI, permissionsAPI, agentRequestsAPI, pocAPI, insightsAPI, journeyAPI, userAPI, tasksAPI, notificationsAPI, attendanceAPI, adminAPI, destinationsAPI, referralAPI, feeAPI, dbToolsAPI, leadsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import CSVImport from '@/components/CSVImport';
import EmailTemplatePreview from '@/components/EmailTemplatePreview';
import JourneyTracker from '@/components/JourneyTracker';
import JourneyDocumentManager from '@/components/JourneyDocumentManager';
import { AdminKPIOverview } from '@/components/KPIDashboard';
import B2CFunnelWidget from '@/components/B2CFunnelWidget';
import { StudentsTab } from '@/components/admin/StudentsTab';
import { AgentsTab } from '@/components/admin/AgentsTab';
import { CoursesTab } from '@/components/admin/CoursesTab';
import { UniversitiesTab } from '@/components/admin/UniversitiesTab';
import { ApplicationsTab } from '@/components/admin/ApplicationsTab';
import SystemHealthTab from '@/components/admin/SystemHealthTab';
import MarketingKit from '@/components/admin/MarketingKit';
import ActivityFeedTimeline from '@/components/ActivityFeedTimeline';
import VisaOutcomesTab from '@/components/admin/VisaOutcomesTab';
import { EmployeesTab } from '@/components/admin/EmployeesTab';
import { DestinationsTab } from '@/components/admin/DestinationsTab';
import { AttendanceTab } from '@/components/admin/AttendanceTab';
import { MyAttendanceTab } from '@/components/admin/MyAttendanceTab';
import JourneyAnalytics from '@/components/admin/JourneyAnalytics';
import AdminScholarshipManager from '@/components/AdminScholarshipManager';
import ForcePasswordChange from '@/components/ForcePasswordChange';
import StudentFinanceTab from '@/components/admin/StudentFinanceTab';
import StaffSavingPotManager from '@/components/admin/StaffSavingPotManager';
import StaffTargetsManager from '@/components/admin/StaffTargetsManager';
import MySavingPot from '@/components/MySavingPot';
import MyTarget from '@/components/MyTarget';
import { AdminLeaveManagement } from '@/components/admin/LeaveManagement';
import { LeaveRequestComponent } from '@/components/employee/LeaveRequest';
import { EmployeeHRPortal } from '@/components/employee/EmployeeHRPortal';
import { LeadManagement } from '@/components/admin/LeadManagement';
import { LeadAnalytics } from '@/components/admin/LeadAnalytics';
import HRManagement from '@/components/admin/HRManagement';
import TaskHub from '@/components/admin/TaskHub';
import AIStudio from '@/components/admin/ai/AIStudio';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import {
  Users,
  FileText,
  Building,
  DollarSign,
  UserPlus,
  CheckCircle,
  Clock,
  TrendingUp,
  Loader2,
  GraduationCap,
  Briefcase,
  MapPin,
  Search,
  Filter,
  Eye,
  MessageSquare,
  ArrowRight,
  Send,
  FileSpreadsheet,
  Download,
  Upload,
  X,
  XCircle,
  Shield,
  StickyNote,
  AlertTriangle,
  User,
  Mail,
  Phone,
  Calendar,
  Trash2,
  Wallet,
  BanknoteIcon,
  CheckCircle2,
  MessageCircle,
  FileDown,
  Plus,
  Pencil,
  Settings,
  BookOpen,
  Megaphone,
  UserCheck,
  BarChart2,
  BarChart3,
  Play,
  Target,
  Award,
  Link2,
  Copy,
  Share2,
  Lock,
  Bell,
  ClipboardList,
  UserCog,
  Key,
  Globe,
  PiggyBank,
  CalendarDays,
  RefreshCw,
  Database,
  Wrench,
  ClipboardX,
  ArrowRightLeft,
  Activity,
  Sparkles
} from 'lucide-react';

// Database Tools Section Component
const DatabaseToolsSection = () => {
  const [normalizing, setNormalizing] = useState(false);
  const [cleaningIntakes, setCleaningIntakes] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState(false);
  const [deletingAttendance, setDeletingAttendance] = useState(false);
  const [fixingIreland, setFixingIreland] = useState(false);
  const [normalizingLevels, setNormalizingLevels] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [attendanceEmail, setAttendanceEmail] = useState('');
  const [results, setResults] = useState(null);

  const handleFixIrelandCountry = async () => {
    if (!window.confirm('This will fix Irish universities/courses that are incorrectly marked as UK. Continue?')) {
      return;
    }
    setFixingIreland(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.fixIrelandCountry();
      setResults({
        type: 'success',
        title: 'Ireland Data Fixed',
        message: `Fixed ${res.data.fixed_universities} universities and ${res.data.fixed_courses} courses. Found ${res.data.irish_universities_found} Irish universities total.`
      });
      toast.success('Ireland country data fixed!');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to fix Ireland data'
      });
      toast.error('Failed to fix Ireland data');
    } finally {
      setFixingIreland(false);
    }
  };

  const handleNormalizeLevels = async () => {
    if (!window.confirm('This will normalize all course levels (bachelors→Bachelors, masters→Masters, phd→PhD). Continue?')) {
      return;
    }
    setNormalizingLevels(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.normalizeLevels();
      setResults({
        type: 'success',
        title: 'Course Levels Normalized',
        message: `Normalized ${res.data.total_fixed} course level entries. Current unique levels: ${res.data.current_unique_levels.join(', ') || 'None'}`
      });
      toast.success('Course levels normalized!');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to normalize levels'
      });
      toast.error('Failed to normalize course levels');
    } finally {
      setNormalizingLevels(false);
    }
  };

  const handleNormalizeCountries = async () => {
    if (!window.confirm('This will update all "UK" entries to "United Kingdom" in universities and courses. Continue?')) {
      return;
    }
    setNormalizing(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.normalizeCountries();
      setResults({
        type: 'success',
        title: 'Countries Normalized',
        message: `Updated ${res.data.universities_updated} universities and ${res.data.courses_updated} courses.`
      });
      toast.success('Countries normalized successfully!');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to normalize countries'
      });
      toast.error('Failed to normalize countries');
    } finally {
      setNormalizing(false);
    }
  };

  const handleCleanupUKIntakes = async () => {
    if (!window.confirm('This will set all UK university intakes to April/May 2026. Continue?')) {
      return;
    }
    setCleaningIntakes(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.cleanupUKIntakes();
      setResults({
        type: 'success',
        title: 'UK Intakes Updated',
        message: `Updated ${res.data.universities_updated} universities and ${res.data.courses_updated} courses to ${res.data.new_intakes.join(', ')}.`
      });
      toast.success('UK intakes cleaned up successfully!');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to cleanup UK intakes'
      });
      toast.error('Failed to cleanup UK intakes');
    } finally {
      setCleaningIntakes(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!employeeEmail.trim()) {
      toast.error('Please enter an employee email');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the employee "${employeeEmail}"? This will also delete their attendance and leave records.`)) {
      return;
    }
    setDeletingEmployee(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.deleteEmployee(employeeEmail.trim());
      setResults({
        type: 'success',
        title: 'Employee Deleted',
        message: res.data.message
      });
      toast.success('Employee deleted successfully!');
      setEmployeeEmail('');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to delete employee'
      });
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    } finally {
      setDeletingEmployee(false);
    }
  };

  const handleDeleteAttendanceByEmail = async () => {
    if (!attendanceEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ALL attendance records for "${attendanceEmail}"? This cannot be undone.`)) {
      return;
    }
    setDeletingAttendance(true);
    setResults(null);
    try {
      const res = await dbToolsAPI.deleteAttendanceByEmail(attendanceEmail.trim());
      setResults({
        type: 'success',
        title: 'Attendance Records Deleted',
        message: res.data.message
      });
      toast.success('Attendance records deleted successfully!');
      setAttendanceEmail('');
    } catch (error) {
      setResults({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to delete attendance records'
      });
      toast.error(error.response?.data?.detail || 'Failed to delete attendance records');
    } finally {
      setDeletingAttendance(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Database className="w-7 h-7" />
          <h2 className="text-xl font-bold">Database Tools</h2>
        </div>
        <p className="text-slate-300 text-sm">One-time maintenance scripts for data cleanup</p>
      </div>

      {/* Results Alert */}
      {results && (
        <div className={`p-4 rounded-xl border-2 ${
          results.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <h4 className={`font-semibold ${results.type === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {results.title}
          </h4>
          <p className={`text-sm mt-1 ${results.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {results.message}
          </p>
        </div>
      )}

      {/* Tools Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Normalize Countries Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
              <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Normalize Countries</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Converts all &quot;UK&quot; entries to &quot;United Kingdom&quot; for consistent filtering. 
                Run this once after deployment.
              </p>
              <Button
                onClick={handleNormalizeCountries}
                disabled={normalizing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="normalize-countries-btn"
              >
                {normalizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Normalizing...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Normalize Countries
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Cleanup UK Intakes Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Cleanup UK Intakes</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Sets all UK university intakes to April/May 2026. Use when intake dates need refreshing.
              </p>
              <Button
                onClick={handleCleanupUKIntakes}
                disabled={cleaningIntakes}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="cleanup-intakes-btn"
              >
                {cleaningIntakes ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Update UK Intakes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Fix Ireland Country Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Fix Ireland Country Data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Fixes Irish universities/courses incorrectly marked as UK (e.g., WIT, Trinity College Dublin).
              </p>
              <Button
                onClick={handleFixIrelandCountry}
                disabled={fixingIreland}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="fix-ireland-btn"
              >
                {fixingIreland ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Fix Ireland Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Normalize Course Levels Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Normalize Course Levels</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Standardizes degree levels (bachelors→Bachelors, masters→Masters, phd→PhD). Removes duplicates.
              </p>
              <Button
                onClick={handleNormalizeLevels}
                disabled={normalizingLevels}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="normalize-levels-btn"
              >
                {normalizingLevels ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Normalizing...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Normalize Levels
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Employee Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-red-200 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Delete Employee</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Remove an employee and all their attendance/leave records by email address.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={employeeEmail}
                  onChange={(e) => setEmployeeEmail(e.target.value)}
                  placeholder="Enter employee email (e.g., john@he-direct.com)"
                  className="flex-1"
                  data-testid="delete-employee-email"
                />
                <Button
                  onClick={handleDeleteEmployee}
                  disabled={deletingEmployee || !employeeEmail.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-employee-btn"
                >
                  {deletingEmployee ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>


        {/* Delete Attendance Records Tool */}
        <div className="bg-white dark:bg-slate-800 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900 flex items-center justify-center flex-shrink-0">
              <ClipboardX className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Delete Attendance Records</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                Remove attendance records by email. Use this for people who appear in attendance logs but are not registered employees.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={attendanceEmail}
                  onChange={(e) => setAttendanceEmail(e.target.value)}
                  placeholder="Enter email (e.g., john@he-direct.com)"
                  className="flex-1"
                  data-testid="delete-attendance-email"
                />
                <Button
                  onClick={handleDeleteAttendanceByEmail}
                  disabled={deletingAttendance || !attendanceEmail.trim()}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="delete-attendance-btn"
                >
                  {deletingAttendance ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Note */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <p className="text-sm text-yellow-700 dark:text-yellow-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          These tools modify database records. They are safe to run multiple times but are typically only needed once after deployment.
        </p>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [applications, setApplications] = useState([]);
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [students, setStudents] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [selectedTab, setSelectedTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'overview';
  });
  // Optional deep-link filters from the funnel widget (e.g. ?stage=deposit)
  const [initialStageParam, setInitialStageParam] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('stage') || null;
  });

  // Keep the URL in sync with tab changes so browser Back/Forward navigate
  // between tabs instead of leaving the admin dashboard entirely.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const currentTab = params.get('tab') || 'overview';
    if (currentTab === selectedTab) return;
    params.set('tab', selectedTab);
    // Clear stage/intake filters when the user navigates via the tab bar
    // (they only make sense when arriving from the funnel deep-link).
    params.delete('stage');
    params.delete('intake');
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ tab: selectedTab }, '', url);
  }, [selectedTab]);

  // Listen for browser Back/Forward → sync the tab state with the URL.
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      const nextTab = params.get('tab') || 'overview';
      setSelectedTab(nextTab);
      setInitialStageParam(params.get('stage') || null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [statusFilter, setStatusFilter] = useState('all');
  const [journeyStageFilter, setJourneyStageFilter] = useState('all');
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('all');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all'); // 'all', 'direct', 'agent'
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAppDetails, setShowAppDetails] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvImportMode, setCsvImportMode] = useState('courses');
  
  // Force password change state
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);

  // Check if user needs to change password on mount
  useEffect(() => {
    if (user?.must_change_password) {
      setShowForcePasswordChange(true);
    }
  }, [user]);

  // Sync tab from URL query params (e.g., /admin?tab=applications)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setSelectedTab(tab);
    }
  }, [location.search]);

  const handlePasswordChangeSuccess = () => {
    setShowForcePasswordChange(false);
    // Refresh user data to update must_change_password flag
    if (refreshUser) refreshUser();
    toast.success('Welcome! You can now access the dashboard.');
  };
  
  // Agent view state (kept for agent detail modal)
  const [showAgentDetails, setShowAgentDetails] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Student notes states
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentNotes, setStudentNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Email preview states
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewType, setEmailPreviewType] = useState('');
  const [emailPreviewData, setEmailPreviewData] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  
  // Payout processing state
  const [processingPayout, setProcessingPayout] = useState(null);
  
  // Application notes state
  const [appNotes, setAppNotes] = useState([]);
  const [newAppNote, setNewAppNote] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [loadingAppNotes, setLoadingAppNotes] = useState(false);
  const [sendingAppNote, setSendingAppNote] = useState(false);
  
  // Course URL management state
  const [coursesWithoutUrls, setCoursesWithoutUrls] = useState([]);
  const [countryCounts, setCountryCounts] = useState([]);
  const [courseUrlCountryFilter, setCourseUrlCountryFilter] = useState('');
  const [editingCourseUrl, setEditingCourseUrl] = useState({});
  const [loadingCourseUrls, setLoadingCourseUrls] = useState(false);
  
  // Course data state (needed for CoursesTab)
  const [allCourses, setAllCourses] = useState([]);
  
  // Destinations/Countries state
  const [destinations, setDestinations] = useState([]);

  // Employee permissions state
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferSummary, setTransferSummary] = useState(null);
  const [loadingTransferSummary, setLoadingTransferSummary] = useState(false);
  const [executingTransfer, setExecutingTransfer] = useState(false);
  const [employeePermissions, setEmployeePermissions] = useState({
    can_manage_applications: true,
    can_manage_students: true,
    can_manage_agents: false,
    can_manage_courses: false,
    can_manage_universities: false,
    can_manage_payouts: false,
    can_view_analytics: true
  });
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Agent Requests state
  const [agentRequests, setAgentRequests] = useState([]);
  const [processingRequestId, setProcessingRequestId] = useState(null);
  
  // Insights state
  const [allInsights, setAllInsights] = useState([]);
  const [showAddInsight, setShowAddInsight] = useState(false);
  const [newInsight, setNewInsight] = useState({
    title: '',
    content: '',
    target_audience: 'all',
    priority: 'normal',
    expires_at: '',
    send_email: true
  });
  const [submittingInsight, setSubmittingInsight] = useState(false);
  
  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Admin Password Reset for Users state
  const [showResetUserPassword, setShowResetUserPassword] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to_id: '',
    priority: 'medium',
    due_date: '',
    category: 'general'
  });
  const [submittingTask, setSubmittingTask] = useState(false);
  
  // Send Notification state
  const [showSendNotification, setShowSendNotification] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    target_type: 'all',
    target_roles: [],
    send_email: false
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  
  // Attendance state
  const [todayAttendance, setTodayAttendance] = useState({ attendance: [], summary: {} });
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceViewMode, setAttendanceViewMode] = useState('daily'); // 'daily', 'weekly', 'monthly', 'range'
  const [attendanceData, setAttendanceData] = useState({ records: [], summary: {}, date_range: null });
  const [attendanceEmployeeFilter, setAttendanceEmployeeFilter] = useState('all'); // 'all' or employee user_id
  const [attendanceDateRange, setAttendanceDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Employee's own attendance state
  const [myAttendanceStatus, setMyAttendanceStatus] = useState(null);
  const [myAttendanceHistory, setMyAttendanceHistory] = useState([]);
  const [officeInfo, setOfficeInfo] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Employee edit/delete state
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState(null);

  // Application delete state
  const [showDeleteAppConfirm, setShowDeleteAppConfirm] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(null);
  const [isDeletingApp, setIsDeletingApp] = useState(false);

  // Destination delete state
  const [showDeleteDestConfirm, setShowDeleteDestConfirm] = useState(false);
  const [deletingDestination, setDeletingDestination] = useState(null);
  const [isDeletingDest, setIsDeletingDest] = useState(false);
  
  // Add Destination state
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [newDestination, setNewDestination] = useState({
    country: '',
    flag_emoji: '',
    description: '',
    avg_tuition_range: '',
    currency: ''
  });
  const [isAddingDest, setIsAddingDest] = useState(false);

  // Referral info state
  const [myReferralInfo, setMyReferralInfo] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);

  const [newEmployee, setNewEmployee] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    position: ''
  });

  useEffect(() => {
    fetchData();
    fetchTasks();
    fetchTodayAttendance();
    fetchNotifications();
    fetchMyReferralInfo(); // Fetch referral code for all employees/admins
    // Fetch employee's own attendance if they are an employee
    if (user?.employee_id && !user?.is_super_admin) {
      fetchMyAttendance();
      fetchOfficeInfo();
    }
  }, [user]);

  const fetchMyReferralInfo = async () => {
    setLoadingReferral(true);
    try {
      const res = await referralAPI.getMyReferral();
      setMyReferralInfo(res.data);
    } catch (error) {
      console.error('Error fetching referral info:', error);
    } finally {
      setLoadingReferral(false);
    }
  };

  const fetchMyAttendance = async () => {
    try {
      const [todayRes, historyRes] = await Promise.all([
        attendanceAPI.getToday().catch(() => ({ data: null })),
        attendanceAPI.getMy().catch(() => ({ data: { records: [] } }))
      ]);
      
      // Set today's status - merge attendance data for display
      if (todayRes.data?.checked_in && todayRes.data.attendance) {
        setMyAttendanceStatus({
          checked_in: true,
          check_out_time: todayRes.data.attendance.check_out_time,
          check_in_time: todayRes.data.attendance.check_in_time,
          check_in_time_pkt: todayRes.data.attendance.check_in_time_pkt,
          check_out_time_pkt: todayRes.data.attendance.check_out_time_pkt,
          office_name: todayRes.data.attendance.check_in_location?.office_name || 
                       (todayRes.data.attendance.location_verified ? 'Office' : 'Remote'),
          location_verified: todayRes.data.attendance.location_verified || 
                            todayRes.data.attendance.check_in_location?.verified
        });
      } else {
        setMyAttendanceStatus(todayRes.data);
      }
      
      // Extract records array from the response
      const historyData = historyRes.data?.records || historyRes.data;
      setMyAttendanceHistory(Array.isArray(historyData) ? historyData : []);
    } catch (error) {
      console.error('Error fetching my attendance:', error);
      setMyAttendanceHistory([]);
    }
  };

  const fetchOfficeInfo = async () => {
    try {
      const res = await attendanceAPI.getOfficeInfo();
      setOfficeInfo(res.data);
    } catch (error) {
      console.error('Error fetching office info:', error);
    }
  };

  const handleEmployeeCheckIn = async () => {
    setCheckingIn(true);
    setLocationError(null);
    
    try {
      // Get current location
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 20000, // high-accuracy GPS fixes are slower to acquire indoors
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      
      const res = await attendanceAPI.checkIn({
        latitude,
        longitude,
        notes: ''
      });
      
      toast.success(`Checked in at ${res.data.check_in_time_pkt} (${res.data.office_name})`);
      fetchMyAttendance();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to check in';
      setLocationError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleEmployeeCheckOut = async () => {
    setCheckingOut(true);
    setLocationError(null);
    
    try {
      // Get current location
      let latitude = null;
      let longitude = null;
      
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 20000, // high-accuracy GPS fixes are slower to acquire indoors
            maximumAge: 0
          });
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (locError) {
        console.log('Location not available for checkout');
      }
      
      const res = await attendanceAPI.checkOut({
        latitude,
        longitude,
        notes: ''
      });
      
      toast.success(`Checked out successfully. Total hours: ${res.data.total_hours}`);
      fetchMyAttendance();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to check out';
      toast.error(errorMsg);
    } finally {
      setCheckingOut(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await tasksAPI.getAll();
      setTasks(res.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchTodayAttendance = async (employeeId = null) => {
    setLoadingAttendance(true);
    try {
      const res = await attendanceAPI.adminGetToday();
      let attendance = res.data?.attendance || [];
      
      // Filter by employee if specified
      if (employeeId && employeeId !== 'all') {
        attendance = attendance.filter(r => r.user_id === employeeId);
      }
      
      setTodayAttendance({
        attendance,
        summary: res.data?.summary || {}
      });
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchWeeklyAttendance = async (employeeId = null) => {
    setLoadingAttendance(true);
    try {
      // Get current week's date range
      const today = new Date();
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const startDate = monday.toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];
      
      // Build query params
      const params = { start_date: startDate, end_date: endDate };
      if (employeeId && employeeId !== 'all') {
        params.user_id = employeeId;
      }
      
      // Fetch all attendance for this week
      const res = await attendanceAPI.adminGetAll(params);
      
      setAttendanceData({
        records: res.data?.records || [],
        summary: res.data?.summary || {},
        date_range: {
          start: monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          end: today.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        }
      });
    } catch (error) {
      console.error('Error fetching weekly attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchMonthlyAttendance = async (employeeId = null) => {
    setLoadingAttendance(true);
    try {
      const today = new Date();
      const month = today.toISOString().slice(0, 7); // "2026-02"
      
      // Build query params
      const params = { month };
      if (employeeId && employeeId !== 'all') {
        params.user_id = employeeId;
      }
      
      const res = await attendanceAPI.adminGetAll(params);
      
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setAttendanceData({
        records: res.data?.records || [],
        summary: res.data?.summary || {},
        date_range: {
          start: firstDay.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          end: today.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        }
      });
    } catch (error) {
      console.error('Error fetching monthly attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Fetch attendance for custom date range
  const fetchAttendanceData = async () => {
    setLoadingAttendance(true);
    try {
      const params = {
        start_date: attendanceDateRange.start,
        end_date: attendanceDateRange.end
      };
      if (attendanceEmployeeFilter && attendanceEmployeeFilter !== 'all') {
        params.user_id = attendanceEmployeeFilter;
      }
      
      const res = await attendanceAPI.adminGetAll(params);
      setAttendanceData({
        records: res.data?.records || [],
        summary: res.data?.summary || {},
        date_range: { start: attendanceDateRange.start, end: attendanceDateRange.end }
      });
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data?.notifications || []);
      setUnreadCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.is_read) {
        await notificationsAPI.markRead(notif.id);
        fetchNotifications();
      }
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
    setShowNotifications(false);

    const type = notif.type || '';
    const ref = notif.reference_id || notif.application_id || notif.student_id;

    // Route to the right tab based on notification type
    if (type === 'application_update' || type === 'agent_note' || type.startsWith('application')) {
      navigate(`/admin?tab=applications${ref ? `&id=${ref}` : ''}`);
    } else if (type === 'student_assigned' || type === 'document_upload' || type === 'document_review' || type === 'message' || type === 'payment_recorded' || type === 'fee_reminder') {
      navigate(`/admin?tab=students${ref ? `&id=${ref}` : ''}`);
    } else if (type === 'task_assignment' || type === 'task_completed' || type === 'mention') {
      navigate(`/admin?tab=overview`);
    } else if (type === 'lead_assigned' || type.startsWith('lead')) {
      navigate(`/admin?tab=leads${ref ? `&id=${ref}` : ''}`);
    } else if (type === 'agent_registered' || type.startsWith('agent')) {
      navigate(`/admin?tab=agents${ref ? `&id=${ref}` : ''}`);
    } else {
      navigate('/admin');
    }
  };

  const fetchData = async () => {
    try {
      // Only fetch essential data on mount — heavy data (courses, insights, destinations) loaded per tab
      const [statsRes, appsRes, agentsRes, employeesRes, uniRes, studentsRes, payoutsRes, agentReqsRes] = await Promise.all([
        statsAPI.getDashboard().catch(() => ({ data: {} })),
        applicationsAPI.getAll({ limit: 1000 }).catch(() => ({ data: [] })),
        agentsAPI.getAll().catch(() => ({ data: [] })),
        employeesAPI.getAll().catch(() => ({ data: [] })),
        universitiesAPI.getAll().catch(() => ({ data: [] })),
        adminStudentsAPI.getAll().catch(() => ({ data: [] })),
        payoutAPI.getAll().catch(() => ({ data: [] })),
        agentRequestsAPI.adminGetAll().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data || {});
      setApplications(appsRes.data || []);
      setAgents(agentsRes.data || []);
      setEmployees(employeesRes.data || []);
      setUniversities(uniRes.data || []);
      setStudents(studentsRes.data || []);
      setPayoutRequests(payoutsRes.data || []);
      setAgentRequests(agentReqsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load heavy data when specific tabs are opened
  const loadedTabs = useRef({});
  useEffect(() => {
    const loadTabData = async () => {
      if (selectedTab === 'courses' && !loadedTabs.current.courses) {
        loadedTabs.current.courses = true;
        try {
          const res = await coursesAPI.getAll();
          setAllCourses(res.data || []);
        } catch { loadedTabs.current.courses = false; }
      }
      if (selectedTab === 'insights' && !loadedTabs.current.insights) {
        loadedTabs.current.insights = true;
        try {
          const res = await insightsAPI.getAll();
          setAllInsights(res.data || []);
        } catch { loadedTabs.current.insights = false; }
      }
      if (selectedTab === 'destinations' && !loadedTabs.current.destinations) {
        loadedTabs.current.destinations = true;
        try {
          const res = await destinationsAPI.getAll();
          setDestinations(res.data || []);
        } catch { loadedTabs.current.destinations = false; }
      }
    };
    loadTabData();
  }, [selectedTab]);

  const handleProcessPayout = async (payoutId, action) => {
    setProcessingPayout(payoutId);
    try {
      await payoutAPI.process(payoutId, action);
      toast.success(`Payout ${action === 'approve' ? 'approved' : action === 'paid' ? 'marked as paid' : 'rejected'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessingPayout(null);
    }
  };

  const getPayoutStatusColor = (status) => {
    const colors = {
      pending: 'bg-teal-100 text-teal-600',
      approved: 'bg-blue-100 text-blue-600',
      paid: 'bg-green-100 text-green-600',
      rejected: 'bg-red-100 text-red-600'
    };
    return colors[status] || colors.pending;
  };

  // Memoized filtered payouts
  const filteredPayouts = useMemo(() => {
    return payoutRequests.filter(p => 
      payoutStatusFilter === 'all' || p.status === payoutStatusFilter
    );
  }, [payoutRequests, payoutStatusFilter]);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await applicationsAPI.updateStatus(appId, newStatus);
      toast.success('Application status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewApplication = async (app) => {
    setSelectedApp(app);
    setShowAppDetails(true);
    setAppNotes([]);
    setNewAppNote('');
    setIsInternalNote(false);
    
    // Fetch notes for this application
    setLoadingAppNotes(true);
    try {
      const notesRes = await applicationsAPI.getNotes(app.id);
      setAppNotes(notesRes.data || []);
    } catch (error) {
      console.error('Error fetching application notes:', error);
    } finally {
      setLoadingAppNotes(false);
    }
  };

  const handleAddAppNote = async () => {
    if (!newAppNote.trim() || !selectedApp) return;
    
    setSendingAppNote(true);
    try {
      await applicationsAPI.addNote(selectedApp.id, {
        content: newAppNote,
        is_internal: isInternalNote
      });
      toast.success(isInternalNote ? 'Internal note added' : 'Message sent to student');
      
      // Refresh notes
      const notesRes = await applicationsAPI.getNotes(selectedApp.id);
      setAppNotes(notesRes.data || []);
      setNewAppNote('');
      setIsInternalNote(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSendingAppNote(false);
    }
  };

  const handleQuickAction = async (appId, action) => {
    const statusMap = {
      'request_docs': 'under_review',
      'pass_counselor': 'under_review',
      'pass_application_team': 'under_review',
      'accept': 'accepted',
      'reject': 'rejected',
      'enroll': 'enrolled'
    };
    
    const newStatus = statusMap[action] || 'under_review';
    
    try {
      await applicationsAPI.updateStatus(appId, newStatus);
      toast.success(`Application ${action.replace('_', ' ')} - Status updated`);
      fetchData();
      setShowAppDetails(false);
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  const handleCSVImportComplete = () => {
    toast.success('Courses imported successfully');
    setShowCSVImport(false);
    fetchData();
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await employeesAPI.create(newEmployee);
      toast.success('Employee added successfully');
      setShowAddEmployee(false);
      setNewEmployee({ full_name: '', email: '', password: '', phone: '', department: '', position: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;
    try {
      const updateData = {
        full_name: editingEmployee.full_name,
        email: editingEmployee.email,
        phone: editingEmployee.phone,
        department: editingEmployee.department,
        position: editingEmployee.position };
      // Only include password if it's been changed
      if (editingEmployee.newPassword) {
        updateData.password = editingEmployee.newPassword;
      }
      await employeesAPI.update(editingEmployee.id, updateData);
      toast.success('Employee updated successfully');
      setShowEditEmployee(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deletingEmployee) return;
    try {
      await employeesAPI.delete(deletingEmployee.id);
      toast.success('Employee deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee({ ...emp, newPassword: '' });
    setShowEditEmployee(true);
  };

  const openDeleteConfirm = (emp) => {
    setDeletingEmployee(emp);
    setShowDeleteConfirm(true);
  };

  // Application delete handlers
  const handleDeleteApplication = async () => {
    if (!deletingApplication) return;
    setIsDeletingApp(true);
    try {
      await applicationsAPI.delete(deletingApplication.id);
      toast.success('Application deleted successfully');
      setShowDeleteAppConfirm(false);
      setDeletingApplication(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setIsDeletingApp(false);
    }
  };

  const openDeleteAppConfirm = (app, e) => {
    if (e) e.stopPropagation();
    setDeletingApplication(app);
    setShowDeleteAppConfirm(true);
  };

  // Destination handlers
  const handleAddDestination = async () => {
    if (!newDestination.country) {
      toast.error('Country name is required');
      return;
    }
    setIsAddingDest(true);
    try {
      await destinationsAPI.create(newDestination);
      toast.success(`Destination "${newDestination.country}" added successfully`);
      setShowAddDestination(false);
      setNewDestination({ country: '', flag_emoji: '', description: '', avg_tuition_range: '', currency: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add destination');
    } finally {
      setIsAddingDest(false);
    }
  };

  // Destination delete handlers
  const handleDeleteDestination = async () => {
    if (!deletingDestination) return;
    setIsDeletingDest(true);
    try {
      await destinationsAPI.delete(deletingDestination.id);
      toast.success(`Destination "${deletingDestination.country}" and all related data deleted`);
      setShowDeleteDestConfirm(false);
      setDeletingDestination(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete destination');
    } finally {
      setIsDeletingDest(false);
    }
  };

  const openDeleteDestConfirm = (dest) => {
    setDeletingDestination(dest);
    setShowDeleteDestConfirm(true);
  };

  // PDF Export function
  const handleExportPDF = (app) => {
    const doc = new jsPDF();
    const lineHeight = 7;
    let y = 20;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(234, 179, 8); // Yellow color
    doc.text('H E Direct Consultants', 105, y, { align: 'center' });
    y += 10;

    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('Application Details', 105, y, { align: 'center' });
    y += 15;

    // Application ID
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`Application ID: ${app.id}`, 20, y);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 190, y, { align: 'right' });
    y += 15;

    // Student Information Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Student Information', 20, y);
    y += 2;
    doc.setDrawColor(234, 179, 8);
    doc.setLineWidth(0.5);
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);
    
    doc.text(`Name: ${app.student_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Email: ${app.student_email || 'N/A'}`, 20, y);
    y += lineHeight;
    if (app.agent_name) {
      doc.text(`Referred by Agent: ${app.agent_name}`, 20, y);
      y += lineHeight;
    }
    y += 8;

    // Course Information Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Course Information', 20, y);
    y += 2;
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);

    doc.text(`Course: ${app.course_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`University: ${app.university_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Country: ${app.country || 'N/A'}`, 20, y);
    y += lineHeight + 8;

    // Application Status Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Application Status', 20, y);
    y += 2;
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const statusColors = {
      enrolled: [34, 197, 94],
      accepted: [59, 130, 246],
      rejected: [239, 68, 68],
      under_review: [234, 179, 8],
      submitted: [249, 115, 22]
    };
    const statusColor = statusColors[app.status] || [100, 100, 100];
    doc.setTextColor(...statusColor);
    doc.text(`Status: ${app.status?.replace('_', ' ').toUpperCase() || 'N/A'}`, 20, y);
    y += lineHeight;

    doc.setTextColor(80);
    doc.text(`Created: ${app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB') : 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Last Updated: ${app.updated_at ? new Date(app.updated_at).toLocaleDateString('en-GB') : 'N/A'}`, 20, y);
    y += lineHeight + 8;

    // Documents Section
    if (app.documents && app.documents.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50);
      doc.text('Uploaded Documents', 20, y);
      y += 2;
      doc.line(20, y, 100, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80);

      app.documents.forEach((docItem, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${index + 1}. ${docItem.type?.replace('_', ' ')}: ${docItem.filename || 'Uploaded'}`, 20, y);
        y += lineHeight;
      });
      y += 8;
    }

    // Notes Section (if any)
    if (app.notes) {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50);
      doc.text('Additional Notes', 20, y);
      y += 2;
      doc.line(20, y, 100, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80);
      
      const splitNotes = doc.splitTextToSize(app.notes, 170);
      splitNotes.forEach(line => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 20, y);
        y += lineHeight;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        'H E Direct Consultants | amin@he-direct.com | +44 7488 420729',
        105,
        290,
        { align: 'center' }
      );
      doc.text(`Page ${i} of ${pageCount}`, 190, 290, { align: 'right' });
    }

    // Save the PDF
    const fileName = `HEDirect_Application_${app.student_name?.replace(/\s+/g, '_') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('PDF exported successfully');
  };

  // Course URL management functions
  const loadCoursesWithoutUrls = async (country = null) => {
    setLoadingCourseUrls(true);
    try {
      const response = await coursesAPI.getWithoutUrls(100, country);
      setCoursesWithoutUrls(response.data?.courses || []);
      setCountryCounts(response.data?.country_counts || []);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoadingCourseUrls(false);
    }
  };

  const handleSaveCourseUrl = async (courseId) => {
    const url = editingCourseUrl[courseId];
    if (!url || !url.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }
    
    try {
      await coursesAPI.updateCourseUrl(courseId, url.trim());
      toast.success('Course URL saved successfully');
      // Remove from list
      setCoursesWithoutUrls(prev => prev.filter(c => c.id !== courseId));
      setEditingCourseUrl(prev => {
        const updated = {...prev};
        delete updated[courseId];
        return updated;
      });
    } catch (error) {
      toast.error('Failed to save course URL');
    }
  };

  // Change Password handler
  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await userAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Employee permissions handlers
  const handleOpenPermissions = async (employee) => {
    setSelectedEmployee(employee);
    setShowPermissionsDialog(true);
    try {
      const response = await permissionsAPI.get(employee.id);
      setEmployeePermissions(response.data.permissions || getDefaultPermissions());
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setEmployeePermissions(getDefaultPermissions());
    }
  };

  const getDefaultPermissions = () => ({
    // Applications - granular
    applications_view: true,
    applications_create: false,
    applications_edit: false,
    applications_delete: false,
    applications_change_status: true,
    applications_assign: false,
    // Students - granular
    students_view: true,
    students_create: false,
    students_edit: false,
    students_delete: false,
    students_add_notes: true,
    // Agents - granular
    agents_view: false,
    agents_approve: false,
    agents_reject: false,
    agents_assign_poc: false,
    agents_view_documents: false,
    // Leads - granular
    leads_view: true,
    leads_create: true,
    leads_edit: true,
    leads_delete: false,
    leads_assign: true,
    leads_convert: true,
    // Courses - granular
    courses_view: true,
    courses_add: false,
    courses_edit: false,
    courses_delete: false,
    // Universities - granular
    universities_view: true,
    universities_add: false,
    universities_edit: false,
    universities_delete: false,
    // Payouts - granular
    payouts_view: false,
    payouts_process: false,
    payouts_approve: false,
    // Analytics - granular
    analytics_view_dashboard: true,
    analytics_export: false,
    // HR Module - granular
    hr_view_employees: false,
    hr_manage_attendance: false,
    hr_manage_leave: false,
    // Tasks - granular
    tasks_view: true,
    tasks_create: true,
    tasks_assign: false,
    // Legacy compatibility
    can_manage_applications: true,
    can_manage_students: true,
    can_manage_agents: false,
    can_manage_courses: false,
    can_manage_universities: false,
    can_manage_payouts: false,
    can_view_analytics: true,
    can_assign_tasks: false
  });

  const handleSavePermissions = async () => {
    if (!selectedEmployee) return;
    setSavingPermissions(true);
    try {
      await permissionsAPI.update(selectedEmployee.id, employeePermissions);
      toast.success('Permissions updated successfully');
      setShowPermissionsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  // Employee Transfer handlers
  const handleOpenTransferDialog = async () => {
    if (!selectedEmployee) return;
    setShowTransferDialog(true);
    setTransferTargetId('');
    setLoadingTransferSummary(true);
    try {
      const res = await employeesAPI.getTransferSummary(selectedEmployee.id);
      setTransferSummary(res.data);
    } catch (error) {
      toast.error('Failed to load transfer summary');
      setTransferSummary(null);
    } finally {
      setLoadingTransferSummary(false);
    }
  };

  const handleExecuteTransfer = async () => {
    if (!selectedEmployee || !transferTargetId) return;
    setExecutingTransfer(true);
    try {
      const res = await employeesAPI.transfer(selectedEmployee.id, transferTargetId);
      toast.success(res.data.message);
      setShowTransferDialog(false);
      setShowPermissionsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setExecutingTransfer(false);
    }
  };

  // Handle agent request processing
  const handleProcessAgentRequest = async (requestId, action) => {
    setProcessingRequestId(requestId);
    try {
      await agentRequestsAPI.adminProcess(requestId, action);
      toast.success(`Request ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setProcessingRequestId(null);
    }
  };

  // Handle creating insights
  const handleCreateInsight = async (e) => {
    e.preventDefault();
    if (!newInsight.title || !newInsight.content) {
      toast.error('Please fill in title and content');
      return;
    }
    setSubmittingInsight(true);
    try {
      const result = await insightsAPI.create({
        ...newInsight,
        expires_at: newInsight.expires_at || null
      });
      if (newInsight.send_email) {
        toast.success('Insight published & email notifications sent!');
      } else {
        toast.success('Insight published successfully');
      }
      setShowAddInsight(false);
      setNewInsight({ title: '', content: '', target_audience: 'all', priority: 'normal', expires_at: '', send_email: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create insight');
    } finally {
      setSubmittingInsight(false);
    }
  };

  // Handle deleting insights
  const handleDeleteInsight = async (insightId) => {
    if (!window.confirm('Are you sure you want to delete this insight?')) return;
    try {
      await insightsAPI.delete(insightId);
      toast.success('Insight deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete insight');
    }
  };

  // Agent approval handlers
  const handleViewAgent = (agent) => {
    setSelectedAgent(agent);
    setRejectionReason('');
    setShowAgentDetails(true);
  };

  const handleAgentStatusUpdate = async (status) => {
    if (!selectedAgent) return;
    
    if (status === 'rejected' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    // Show email preview before sending
    const previewType = status === 'approved' ? 'agent_approved' : 
                        status === 'rejected' ? 'agent_rejected' : 'agent_compliance';
    
    setEmailPreviewType(previewType);
    setEmailPreviewData({
      agentEmail: selectedAgent.email,
      agentName: selectedAgent.full_name,
      reason: rejectionReason
    });
    setPendingAction({ type: 'agent_status', status });
    setShowEmailPreview(true);
  };

  const executeAgentStatusUpdate = async () => {
    if (!selectedAgent || !pendingAction) return;
    
    try {
      await agentsAPI.updateStatus(selectedAgent.id, pendingAction.status, rejectionReason);
      toast.success(`Agent ${pendingAction.status === 'approved' ? 'approved' : pendingAction.status === 'rejected' ? 'rejected' : 'sent for compliance review'}`);
      setShowAgentDetails(false);
      setSelectedAgent(null);
      setRejectionReason('');
      setShowEmailPreview(false);
      setPendingAction(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const handleAgentStatusUpdateDirect = async (status) => {
    if (!selectedAgent) return;
    
    if (status === 'rejected' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await agentsAPI.updateStatus(selectedAgent.id, status, rejectionReason);
      toast.success(`Agent ${status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'sent for compliance review'}`);
      setShowAgentDetails(false);
      setSelectedAgent(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  // Student notes handlers
  const handleViewStudent = async (student) => {
    setSelectedStudent(student);
    setShowStudentDetails(true);
    setLoadingNotes(true);
    setNewNote('');
    setNoteType('internal');
    
    try {
      const response = await adminNotesAPI.get('student', student.id);
      setStudentNotes(response.data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setStudentNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedStudent) return;

    // If sending to student, show email preview
    if (noteType === 'student_visible') {
      setEmailPreviewType('student_note');
      setEmailPreviewData({
        studentEmail: selectedStudent.email,
        studentName: selectedStudent.full_name,
        note: newNote
      });
      setPendingAction({ type: 'student_note' });
      setShowEmailPreview(true);
      return;
    }

    // Internal note - add directly
    await executeAddNote();
  };

  const executeAddNote = async () => {
    if (!newNote.trim() || !selectedStudent) return;

    try {
      await adminNotesAPI.add('student', selectedStudent.id, {
        note: newNote,
        note_type: noteType
      });
      toast.success(noteType === 'student_visible' ? 'Note added and sent to student' : 'Internal note added');
      setNewNote('');
      setShowEmailPreview(false);
      setPendingAction(null);
      
      // Refresh notes
      const response = await adminNotesAPI.get('student', selectedStudent.id);
      setStudentNotes(response.data || []);
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleEmailPreviewConfirm = async () => {
    if (!pendingAction) return;
    
    if (pendingAction.type === 'agent_status') {
      await executeAgentStatusUpdate();
    } else if (pendingAction.type === 'student_note') {
      await executeAddNote();
    }
  };

  const handleEmailPreviewClose = () => {
    setShowEmailPreview(false);
    setPendingAction(null);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await adminNotesAPI.delete(noteId);
      toast.success('Note deleted');
      setStudentNotes(studentNotes.filter(n => n.id !== noteId));
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-600',
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-amber-100 text-amber-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      enrolled: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || colors.draft;
  };

  const getAgentStatusColor = (status) => {
    const colors = {
      pending: 'bg-teal-100 text-teal-600',
      approved: 'bg-green-100 text-green-600',
      rejected: 'bg-red-100 text-red-600',
      compliance_review: 'bg-orange-100 text-orange-600'
    };
    return colors[status] || colors.pending;
  };

  const pendingStatuses = ['submitted', 'under_review', 'document_requested', 'passed_to_counselor', 'passed_to_app_team'];
  
  // Filter applications by status, journey stage, and source type
  // Memoized filtered applications - prevents recalculation on every render
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Status filter
      const statusMatch = statusFilter === 'all' 
        ? true 
        : statusFilter === 'pending'
        ? pendingStatuses.includes(app.status)
        : app.status === statusFilter;
      
      // Journey stage filter
      const stageMatch = journeyStageFilter === 'all' 
        ? true 
        : (app.journey_stage || 'inquiry') === journeyStageFilter;
      
      // Source type filter (direct vs agent)
      const sourceMatch = sourceTypeFilter === 'all'
        ? true
        : sourceTypeFilter === 'direct'
        ? !app.agent_id
        : !!app.agent_id;
      
      return statusMatch && stageMatch && sourceMatch;
    });
  }, [applications, statusFilter, journeyStageFilter, sourceTypeFilter]);

  // Memoized pending counts to avoid recalculating in render
  const pendingCounts = useMemo(() => ({
    pendingAgents: agents.filter(a => (a.approval_status || 'approved') === 'pending').length,
    pendingApplications: applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length,
    pendingPayouts: payoutRequests.filter(p => p.status === 'pending').length
  }), [agents, applications, payoutRequests]);

  // Handler for clicking on journey funnel stage
  const handleJourneyStageClick = (stage) => {
    setJourneyStageFilter(stage);
    setSelectedTab('applications');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      {/* Force Password Change Modal */}
      <ForcePasswordChange 
        isOpen={showForcePasswordChange}
        onSuccess={handlePasswordChangeSuccess}
        userEmail={user?.email}
      />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800 mb-1">
                {user?.is_super_admin ? 'Admin Dashboard' : 'Employee Dashboard'}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {user?.is_super_admin ? 'Manage applications, agents, and employees' : 'Manage applications and students'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <Button
                  onClick={() => setShowNotifications(!showNotifications)}
                  variant="outline"
                  className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 relative"
                  data-testid="notification-bell"
                >
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                    <div className="p-3 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
                      <span className="font-medium text-gray-800 dark:text-white">Notifications</span>
                      {unreadCount > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={async () => {
                            await notificationsAPI.markAllRead();
                            fetchNotifications();
                          }}
                          className="text-xs text-teal-600"
                        >
                          Mark all read
                        </Button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                          No notifications
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`p-3 border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer ${!notif.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                            onClick={() => handleNotificationClick(notif)}
                            data-testid={`notification-item-${notif.id}`}
                          >
                            <p className="font-medium text-sm text-gray-800 dark:text-white">{notif.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Button
                onClick={() => setShowChangePassword(true)}
                variant="outline"
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button
                onClick={() => window.location.href = '/training'}
                variant="outline"
                className="border-yellow-500/50 text-teal-600 hover:bg-teal-500/10"
                data-testid="training-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Training
              </Button>
              <Button
                onClick={() => window.location.href = '/admin/analytics'}
                className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white hover:from-yellow-400 hover:to-yellow-300"
                data-testid="view-analytics-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                View Analytics
              </Button>
              {(user?.is_super_admin || ['kamran@hedirect.org','ishaq@hedirect.org','syedfarhan@hedirect.org','ashir@hedirect.org'].includes((user?.email || '').toLowerCase())) && (
                <Button
                  onClick={() => window.location.href = '/admin/pnl-calculator'}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-md"
                  data-testid="pnl-calculator-btn"
                  title="Business P&L Calculator (restricted)"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  P&amp;L Calculator
                </Button>
              )}
              <Badge className="bg-teal-600/20 text-teal-600 text-sm px-3 py-1">
                Administrator
              </Badge>
            </div>
          </div>

          {/* Stats Grid - with bordered section */}
          <div className="bg-white border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-8 dark:bg-slate-800 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Dashboard Overview</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Key metrics at a glance</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div 
                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
                onClick={() => setSelectedTab('students')}
                data-testid="stat-card-students"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.total_students || 0}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Students</p>
                  </div>
                </div>
              </div>
              
              <div 
                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5 cursor-pointer hover:border-purple-400 hover:shadow-md transition-all"
                onClick={() => setSelectedTab('agents')}
                data-testid="stat-card-agents"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.total_agents || agents.length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Agents</p>
                  </div>
                </div>
              </div>
              
              <div 
                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5 cursor-pointer hover:border-teal-400 hover:shadow-md transition-all"
                onClick={() => setSelectedTab('applications')}
                data-testid="stat-card-applications"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.total_applications || applications.length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Applications</p>
                  </div>
                </div>
              </div>
              
              <div 
                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5 cursor-pointer hover:border-orange-400 hover:shadow-md transition-all"
                onClick={() => { setSelectedTab('applications'); setInitialStageParam(null); }}
                data-testid="stat-card-pending"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.pending_applications || 0}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                  </div>
                </div>
              </div>
              
              <div 
                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5 cursor-pointer hover:border-green-400 hover:shadow-md transition-all"
                onClick={() => { setSelectedTab('applications'); setInitialStageParam('offer_accepted'); }}
                data-testid="stat-card-accepted"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.accepted_applications || 0}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Accepted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs - with bordered container */}
          <div className="bg-white border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 dark:bg-slate-800 transition-colors">
            <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
              <div className="border-b border-gray-200 dark:border-slate-600 pb-4 mb-2">
                <TabsList className="bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 p-1.5 flex flex-wrap gap-1 rounded-xl h-auto justify-start items-center">
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="ai-studio" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-ai-studio">
                <Sparkles className="w-4 h-4 mr-1" />
                AI Studio
              </TabsTrigger>
              {(user?.is_super_admin || user?.permissions?.leads_view || user?.permissions?.can_manage_applications) && (
              <TabsTrigger value="leads" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-leads">
                <Target className="w-4 h-4 mr-1" />
                Leads
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.leads_view || user?.permissions?.can_manage_applications) && (
              <TabsTrigger value="lead-analytics" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-lead-analytics">
                <BarChart3 className="w-4 h-4 mr-1" />
                Lead Analytics
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.applications_view || user?.permissions?.can_manage_applications) && (
              <TabsTrigger value="applications" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-applications">
                Applications
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.students_view || user?.permissions?.can_manage_students) && (
              <TabsTrigger value="students" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-students">
                Students
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.agents_view || user?.permissions?.can_manage_agents) && (
              <TabsTrigger value="agents" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-agents">
                Agents
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.payouts_view || user?.permissions?.can_manage_payouts) && (
              <TabsTrigger value="payouts" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-payouts">
                <Wallet className="w-4 h-4 mr-1" />
                Payouts
                {payoutRequests.filter(p => p.status === 'pending').length > 0 && (
                  <Badge className="ml-2 bg-red-500/80 text-white text-xs px-1.5">
                    {payoutRequests.filter(p => p.status === 'pending').length}
                  </Badge>
                )}
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.hr_view_employees) && (
              <TabsTrigger value="employees" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-employees">
                Employees
              </TabsTrigger>
              )}
              {user?.is_super_admin && (
              <TabsTrigger value="system-health" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-system-health">
                <Activity className="w-4 h-4 mr-1" />
                System Health
              </TabsTrigger>
              )}
              {user?.is_super_admin && (
              <TabsTrigger value="marketing-kit" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-marketing-kit">
                <Sparkles className="w-4 h-4 mr-1" />
                Marketing Kit
              </TabsTrigger>
              )}
              <TabsTrigger value="visa-outcomes" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-visa-outcomes">
                <Sparkles className="w-4 h-4 mr-1" />
                Visa Outcomes
              </TabsTrigger>
              {(user?.is_super_admin || user?.permissions?.universities_view || user?.permissions?.can_manage_universities) && (
              <TabsTrigger value="universities" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-universities">
                Universities
              </TabsTrigger>
              )}
              {user?.is_super_admin && (
              <TabsTrigger value="destinations" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-destinations">
                <Globe className="w-4 h-4 mr-1" />
                Countries
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.can_manage_courses || user?.permissions?.courses_view) && (
              <TabsTrigger value="courses" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-courses">
                <BookOpen className="w-4 h-4 mr-1" />
                Courses
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.can_manage_courses) && (
              <TabsTrigger value="course-urls" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-course-urls">
                Course URLs
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.can_manage_agents) && (
              <TabsTrigger value="agent-requests" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-agent-requests">
                Agent Requests
              </TabsTrigger>
              )}
              <TabsTrigger value="kpi-dashboard" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-kpi">
                <Target className="w-4 h-4 mr-1" />
                KPI Dashboard
              </TabsTrigger>
              {(user?.is_super_admin || user?.permissions?.can_view_analytics) && (
              <TabsTrigger value="journey-analytics" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-journey-analytics">
                <BarChart2 className="w-4 h-4 mr-1" />
                Journey Analytics
              </TabsTrigger>
              )}
              {user?.is_super_admin && (
              <TabsTrigger value="insights" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-insights">
                <Megaphone className="w-4 h-4 mr-1" />
                Insights
              </TabsTrigger>
              )}
              {(user?.is_super_admin || user?.permissions?.can_manage_courses || user?.permissions?.courses_view) && (
              <TabsTrigger value="scholarships" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-scholarships">
                <Award className="w-4 h-4 mr-1" />
                Scholarships
              </TabsTrigger>
              )}
              {user?.is_super_admin && (
              <TabsTrigger value="attendance" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-attendance">
                <Calendar className="w-4 h-4 mr-1" />
                Attendance
              </TabsTrigger>
              )}
              {/* Leave Management for super admins */}
              {user?.is_super_admin && (
              <TabsTrigger value="leave-management" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-leave-management">
                <CalendarDays className="w-4 h-4 mr-1" />
                Leave Requests
              </TabsTrigger>
              )}
              {/* HR Management for super admins */}
              {user?.is_super_admin && (
              <TabsTrigger value="hr-management" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-hr-management">
                <Users className="w-4 h-4 mr-1" />
                HR Module
              </TabsTrigger>
              )}
              {/* Task Hub for super admins OR employees with task permission */}
              {(user?.is_super_admin || user?.permissions?.can_assign_tasks) && (
              <TabsTrigger value="task-hub" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-task-hub">
                <ClipboardList className="w-4 h-4 mr-1" />
                Task Hub
              </TabsTrigger>
              )}
              {/* Saving Pots tab for super admins */}
              {user?.is_super_admin && (
              <TabsTrigger value="saving-pots" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-saving-pots">
                <PiggyBank className="w-4 h-4 mr-1" />
                Saving Pots
              </TabsTrigger>
              )}
              {/* Fee Targets tab for super admins */}
              {user?.is_super_admin && (
              <TabsTrigger value="fee-targets" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-fee-targets">
                <Target className="w-4 h-4 mr-1" />
                Fee Targets
              </TabsTrigger>
              )}
              {/* My Attendance tab for employees (non-super admins with employee_id) */}
              {!user?.is_super_admin && user?.employee_id && (
              <TabsTrigger value="my-attendance" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-my-attendance">
                <Clock className="w-4 h-4 mr-1" />
                My Attendance
              </TabsTrigger>
              )}
              {/* My Leave tab for employees */}
              {user?.employee_id && (
              <TabsTrigger value="my-leave" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-my-leave">
                <CalendarDays className="w-4 h-4 mr-1" />
                My Leave
              </TabsTrigger>
              )}
              {/* My HR Portal tab for employees */}
              {user?.employee_id && (
              <TabsTrigger value="my-hr" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-my-hr">
                <User className="w-4 h-4 mr-1" />
                My HR
              </TabsTrigger>
              )}
              {/* Database Tools tab for super admins */}
              {user?.is_super_admin && (
              <TabsTrigger value="db-tools" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300" data-testid="tab-db-tools">
                <Settings className="w-4 h-4 mr-1" />
                DB Tools
              </TabsTrigger>
              )}
            </TabsList>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {/* B2C Recruitment Funnel — top of Overview, visible to every employee */}
              <div className="mb-6">
                <B2CFunnelWidget isAdmin={!!(user?.is_super_admin || user?.role === 'admin' || user?.role === 'super_admin')} />
              </div>
              {/* Quick Actions Panel */}
              <div className="bg-gray-50 dark:bg-slate-700 border-2 border-teal-200 dark:border-teal-800 rounded-xl p-6 mb-6" data-testid="quick-actions-panel">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-teal-200 dark:border-teal-700">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Quick Actions</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Urgent items requiring attention</span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Pending Agent Approvals */}
                  <div 
                    className="bg-white dark:bg-slate-800 rounded-xl p-4 hover:shadow-md cursor-pointer transition-all border-2 border-gray-200 dark:border-slate-600 hover:border-purple-400"
                    onClick={() => setSelectedTab('agents')}
                    data-testid="quick-action-agents"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Agent Approvals</span>
                      </div>
                      {agents.filter(a => (a.approval_status || 'approved') === 'pending').length > 0 && (
                        <Badge className="bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 animate-pulse">
                          {agents.filter(a => (a.approval_status || 'approved') === 'pending').length} pending
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      {agents.filter(a => (a.approval_status || 'approved') === 'pending').slice(0, 2).map(agent => (
                        <div key={agent.id} className="flex items-center gap-2 text-sm">
                          <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
                            <span className="text-purple-600 dark:text-purple-400 text-xs font-semibold">{agent.full_name?.charAt(0)}</span>
                          </div>
                          <span className="text-gray-500 dark:text-gray-400 truncate">{agent.full_name}</span>
                        </div>
                      ))}
                      {agents.filter(a => (a.approval_status || 'approved') === 'pending').length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          All agents approved
                        </p>
                      )}
                      {agents.filter(a => (a.approval_status || 'approved') === 'pending').length > 2 && (
                        <p className="text-purple-600 text-xs flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          +{agents.filter(a => (a.approval_status || 'approved') === 'pending').length - 2} more
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Applications Awaiting Review */}
                  <div 
                    className="bg-white dark:bg-slate-800 rounded-xl p-4 hover:shadow-md cursor-pointer transition-all border-2 border-gray-200 dark:border-slate-600 hover:border-orange-400"
                    onClick={() => { setSelectedTab('applications'); setStatusFilter('submitted'); }}
                    data-testid="quick-action-applications"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Awaiting Review</span>
                      </div>
                      {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length > 0 && (
                        <Badge className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400 animate-pulse">
                          {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length} pending
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).slice(0, 2).map(app => (
                        <div key={app.id} className="flex items-center gap-2 text-sm">
                          <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                          </div>
                          <span className="text-gray-500 dark:text-gray-400 truncate">{app.student_name}</span>
                        </div>
                      ))}
                      {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          All applications reviewed
                        </p>
                      )}
                      {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length > 2 && (
                        <p className="text-orange-600 dark:text-orange-400 text-xs flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          +{applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length - 2} more
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Payout Requests */}
                  <div 
                    className="bg-white dark:bg-slate-800 rounded-xl p-4 hover:shadow-md cursor-pointer transition-all border-2 border-gray-200 dark:border-slate-600 hover:border-green-400"
                    onClick={() => setSelectedTab('payouts')}
                    data-testid="quick-action-payouts"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Payout Requests</span>
                      </div>
                      {payoutRequests.filter(p => p.status === 'pending').length > 0 && (
                        <Badge className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 animate-pulse">
                          {payoutRequests.filter(p => p.status === 'pending').length} pending
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      {payoutRequests.filter(p => p.status === 'pending').slice(0, 2).map(payout => (
                        <div key={payout.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400 truncate">{payout.agent_name}</span>
                          <span className="text-green-600 dark:text-green-400 font-medium">£{payout.amount?.toLocaleString()}</span>
                        </div>
                      ))}
                      {payoutRequests.filter(p => p.status === 'pending').length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                          No pending payouts
                        </p>
                      )}
                      {payoutRequests.filter(p => p.status === 'pending').length > 2 && (
                        <p className="text-green-600 dark:text-green-400 text-xs flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          +{payoutRequests.filter(p => p.status === 'pending').length - 2} more
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Applications */}
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Applications</h3>
                  <div className="space-y-3">
                    {applications.slice(0, 5).map((app) => (
                      <div key={app.id} className="flex items-center justify-between p-3 bg-[#e9ecef]/50 rounded-lg">
                        <div>
                          <p className="text-gray-800 font-medium">{app.student_name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{app.course_name}</p>
                        </div>
                        <Badge className={getStatusColor(app.status)}>
                          {app.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Agents */}
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Agents</h3>
                  <div className="space-y-3">
                    {agents.slice(0, 5).map((agent) => (
                      <div key={agent.id} className="flex items-center justify-between p-3 bg-[#e9ecef]/50 rounded-lg">
                        <div>
                          <p className="text-gray-800 font-medium">{agent.full_name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{agent.company_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-teal-600 font-semibold">{agent.total_referrals || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">referrals</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Your Unique Referral Link - For tracking student leads */}
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Your Referral Link</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Share this unique link to track your student leads</p>
                  </div>
                  {myReferralInfo?.total_referrals > 0 && (
                    <div className="text-right">
                      <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">{myReferralInfo.total_referrals}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">referrals</div>
                    </div>
                  )}
                </div>
                
                {loadingReferral ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    <span className="ml-2 text-gray-500">Loading referral info...</span>
                  </div>
                ) : myReferralInfo?.referral_code ? (
                  <>
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Your Referral Code</span>
                        </div>
                        <Badge className="bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-mono font-bold">
                          {myReferralInfo.referral_code}
                        </Badge>
                      </div>
                      <code className="text-sm text-indigo-600 dark:text-indigo-400 block truncate" data-testid="referral-url">
                        {`${typeof window !== 'undefined' ? window.location.origin : 'https://crm.hedirect.org'}/register?ref=${myReferralInfo.referral_code}`}
                      </code>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => {
                          const referralUrl = `${window.location.origin}/register?ref=${myReferralInfo.referral_code}`;
                          navigator.clipboard.writeText(referralUrl);
                          toast.success('Referral link copied!');
                        }}
                        className="bg-indigo-600 text-white hover:bg-indigo-700"
                        data-testid="copy-referral-link-btn"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Referral Link
                      </Button>
                      <Button
                        onClick={() => {
                          const referralUrl = `${window.location.origin}/register?ref=${myReferralInfo.referral_code}`;
                          const text = `Join H E Direct Consultants and start your study abroad journey! Register here: ${referralUrl}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                          window.open(whatsappUrl, '_blank');
                        }}
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                        data-testid="share-referral-whatsapp-btn"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Share via WhatsApp
                      </Button>
                      <Button
                        onClick={() => {
                          const referralUrl = `${window.location.origin}/register?ref=${myReferralInfo.referral_code}`;
                          const subject = 'Register with H E Direct Consultants';
                          const body = `Hello,\n\nYou can register for study abroad services with H E Direct Consultants using my referral link:\n\n${referralUrl}\n\nBest regards`;
                          window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                        }}
                        variant="outline"
                        className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                        data-testid="share-referral-email-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Share via Email
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Student Registration URL</span>
                    </div>
                    <code className="text-sm text-indigo-600 dark:text-indigo-400 block truncate">
                      {typeof window !== 'undefined' ? `${window.location.origin}/register` : '/register'}
                    </code>
                    <p className="text-xs text-gray-500 mt-2">General link - no tracking</p>
                  </div>
                )}
              </div>

            </TabsContent>

            {/* Leads Tab */}
            <TabsContent value="leads">
              <LeadManagement />
            </TabsContent>

            {/* Lead Analytics Tab */}
            <TabsContent value="lead-analytics">
              <LeadAnalytics />
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <ApplicationsTab 
                applications={applications}
                onRefresh={fetchData}
                user={user}
                initialJourneyStage={initialStageParam}
              />
            </TabsContent>

            {/* Students Tab */}
            <TabsContent value="students">
              <StudentsTab 
                students={students} 
                onRefresh={fetchData} 
                onViewStudent={handleViewStudent}
                isSuperAdmin={user?.is_super_admin}
              />
            </TabsContent>

            {/* Agents Tab */}
            <TabsContent value="agents">
              <AgentsTab 
                agents={agents}
                employees={employees}
                onRefresh={fetchData}
                user={user}
                isSuperAdmin={user?.is_super_admin}
              />
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Agent Payout Requests</h3>
                    <p className="text-gray-500 text-sm">Review and process agent commission payouts</p>
                  </div>
                  <Select value={payoutStatusFilter} onValueChange={setPayoutStatusFilter}>
                    <SelectTrigger className="w-48 bg-white border-gray-200" data-testid="payout-status-filter">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#f1f3f5] border-gray-200">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Payout Summary Stats */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-teal-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-teal-600" />
                      <span className="text-teal-600 font-medium">Pending</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {payoutRequests.filter(p => p.status === 'pending').length}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-600 font-medium">Approved</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {payoutRequests.filter(p => p.status === 'approved').length}
                    </p>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-green-600 font-medium">Paid</span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {payoutRequests.filter(p => p.status === 'paid').length}
                    </p>
                  </div>
                  <div className="p-4 bg-[#e9ecef]/50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Wallet className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-500 font-medium">Total Requested</span>
                    </div>
                    <p className="text-2xl font-semibold text-teal-600">
                      £{payoutRequests.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {filteredPayouts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Agent</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Amount</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Bank Details</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Requested</th>
                          <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                          <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayouts.map((payout) => {
                          const agent = agents.find(a => a.id === payout.agent_id);
                          return (
                            <tr key={payout.id} className="border-b border-white/5 hover:bg-[#e9ecef]/30">
                              <td className="py-4 px-4">
                                <div>
                                  <p className="text-gray-800 font-medium">{agent?.full_name || 'Unknown Agent'}</p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">{agent?.company_name || ''}</p>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <p className="text-xl font-semibold text-teal-600">
                                  £{(payout.amount || 0).toLocaleString()}
                                </p>
                              </td>
                              <td className="py-4 px-4">
                                <p className="text-gray-600 text-sm max-w-xs truncate" title={payout.bank_details}>
                                  {payout.bank_details || 'Not provided'}
                                </p>
                                {payout.notes && (
                                  <p className="text-gray-500 text-xs mt-1">Note: {payout.notes}</p>
                                )}
                              </td>
                              <td className="py-4 px-4">
                                <p className="text-gray-600 text-sm">
                                  {new Date(payout.requested_at).toLocaleDateString()}
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {new Date(payout.requested_at).toLocaleTimeString()}
                                </p>
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={getPayoutStatusColor(payout.status)}>
                                  {payout.status}
                                </Badge>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  {payout.status === 'pending' && (
                                    <>
                                      <Button
                                        size="sm"
                                        onClick={() => handleProcessPayout(payout.id, 'approve')}
                                        disabled={processingPayout === payout.id}
                                        className="bg-blue-600 hover:bg-blue-500 text-white text-xs"
                                        data-testid={`approve-payout-${payout.id}`}
                                      >
                                        {processingPayout === payout.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                        )}
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleProcessPayout(payout.id, 'reject')}
                                        disabled={processingPayout === payout.id}
                                        className="border-red-500/50 text-red-600 hover:bg-red-500/10 text-xs"
                                        data-testid={`reject-payout-${payout.id}`}
                                      >
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  {payout.status === 'approved' && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleProcessPayout(payout.id, 'paid')}
                                      disabled={processingPayout === payout.id}
                                      className="bg-green-600 hover:bg-green-500 text-white text-xs"
                                      data-testid={`mark-paid-${payout.id}`}
                                    >
                                      {processingPayout === payout.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <BanknoteIcon className="w-3 h-3 mr-1" />
                                      )}
                                      Mark as Paid
                                    </Button>
                                  )}
                                  {(payout.status === 'paid' || payout.status === 'rejected') && (
                                    <span className="text-gray-500 text-sm">No actions</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-4">
                      <Wallet className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Payout Requests</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {payoutStatusFilter === 'all' 
                        ? 'No payout requests from agents yet' 
                        : `No ${payoutStatusFilter} payout requests`}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Employees Tab */}
            <TabsContent value="employees">
              <EmployeesTab 
                employees={employees}
                onRefresh={fetchData}
                onOpenPermissions={handleOpenPermissions}
              />
            </TabsContent>

            {/* Universities Tab */}
            <TabsContent value="universities">
              <UniversitiesTab 
                universities={universities}
                onRefresh={fetchData}
                onShowCSVImport={() => { setCsvImportMode('universities'); setShowCSVImport(true); }}
                user={user}
              />
            </TabsContent>

            {/* Destinations/Countries Tab */}
            <TabsContent value="destinations">
              <DestinationsTab 
                destinations={destinations}
                universities={universities}
                onRefresh={fetchData}
                user={user}
              />
            </TabsContent>

            {/* Courses Tab */}
            <TabsContent value="courses">
              <CoursesTab 
                courses={allCourses}
                universities={universities}
                onRefresh={fetchData}
                onShowCSVImport={() => { setCsvImportMode('courses'); setShowCSVImport(true); }}
              />
            </TabsContent>

            {/* Course URLs Tab */}
            <TabsContent value="course-urls">
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Course URL Verification</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Add verified links to official university course pages</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Select 
                        value={courseUrlCountryFilter} 
                        onValueChange={(val) => {
                          setCourseUrlCountryFilter(val);
                          if (val) loadCoursesWithoutUrls(val === 'all' ? null : val);
                        }}
                      >
                        <SelectTrigger className="w-40 bg-[#e9ecef] border-gray-300 text-gray-800 dark:text-white">
                          <SelectValue placeholder="Filter by Country" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#e9ecef] border-gray-300">
                          <SelectItem value="all">All Countries</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="Canada">Canada</SelectItem>
                          <SelectItem value="Australia">Australia</SelectItem>
                          <SelectItem value="USA">USA</SelectItem>
                          <SelectItem value="Germany">Germany</SelectItem>
                          <SelectItem value="France">France</SelectItem>
                          <SelectItem value="Netherlands">Netherlands</SelectItem>
                          <SelectItem value="Spain">Spain</SelectItem>
                          <SelectItem value="Ireland">Ireland</SelectItem>
                          <SelectItem value="New Zealand">New Zealand</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={() => loadCoursesWithoutUrls(courseUrlCountryFilter === 'all' ? null : courseUrlCountryFilter)}
                        disabled={loadingCourseUrls}
                        className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                        data-testid="load-courses-btn"
                      >
                        {loadingCourseUrls ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Load Courses
                      </Button>
                    </div>
                  </div>

                  {/* Country Stats */}
                  {countryCounts.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {countryCounts.map((cc) => (
                        <button
                          key={cc._id}
                          onClick={() => {
                            setCourseUrlCountryFilter(cc._id);
                            loadCoursesWithoutUrls(cc._id);
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            courseUrlCountryFilter === cc._id 
                              ? 'bg-teal-600 text-white' 
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {cc._id || 'Unknown'}: {cc.count} courses
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="bg-teal-600/10 border border-teal-300 rounded-lg p-4 mb-6">
                    <p className="text-teal-600 text-sm">
                      <strong>Instructions:</strong> For each course, find the exact course page on the university&apos;s official website and paste the URL below. 
                      Only verified direct links will appear on the courses page for students.
                    </p>
                  </div>

                  {coursesWithoutUrls.length > 0 ? (
                    <div className="space-y-4">
                      {coursesWithoutUrls.map((course) => (
                        <div key={course.id} className="bg-[#e9ecef]/50 rounded-lg p-4 border border-gray-200">
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-gray-800 font-medium truncate">{course.name}</h4>
                              <p className="text-gray-500 text-sm">{course.university_name}</p>
                              {course.university_website && (
                                <a 
                                  href={course.university_website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 text-xs hover:underline"
                                >
                                  Open university course finder →
                                </a>
                              )}
                            </div>
                            <div className="flex gap-2 flex-1">
                              <Input
                                placeholder="Paste verified course URL here..."
                                value={editingCourseUrl[course.id] || ''}
                                onChange={(e) => setEditingCourseUrl(prev => ({...prev, [course.id]: e.target.value}))}
                                className="bg-gray-100 border-gray-300 text-gray-800 flex-1"
                              />
                              <Button
                                onClick={() => handleSaveCourseUrl(course.id)}
                                className="bg-green-500 hover:bg-green-400 text-gray-800 dark:text-white"
                                disabled={!editingCourseUrl[course.id]}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Click &quot;Load Courses&quot; to see courses that need URL verification</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Agent Requests Tab */}
            <TabsContent value="agent-requests">
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Agent University/Course Requests</h3>
                  <p className="text-gray-500 text-sm mb-6">Review and process requests from agents for new universities or courses to be added to the platform.</p>
                  
                  {agentRequests.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No pending requests from agents</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {agentRequests.map((req) => (
                        <div key={req.id} className="bg-[#e9ecef]/50 rounded-xl p-5" data-testid={`agent-request-${req.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={req.request_type === 'university' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}>
                                  {req.request_type}
                                </Badge>
                                <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-600' : req.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-600'}>
                                  {req.status}
                                </Badge>
                              </div>
                              <h4 className="text-gray-800 font-medium mb-1">{req.university_name}</h4>
                              {req.course_name && <p className="text-gray-500 text-sm">Course: {req.course_name}</p>}
                              {req.country && <p className="text-gray-500 text-sm">Country: {req.country}</p>}
                              <p className="text-gray-500 text-sm mt-2">
                                Submitted by: <span className="text-gray-600 dark:text-gray-300">{req.agent_name}</span> ({req.agent_company})
                              </p>
                              {req.additional_info && (
                                <p className="text-gray-500 text-sm mt-2 p-2 bg-gray-100 rounded">
                                  {req.additional_info}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              {req.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleProcessAgentRequest(req.id, 'approve')}
                                    disabled={processingRequestId === req.id}
                                    className="bg-green-600 hover:bg-green-500 text-white"
                                  >
                                    {processingRequestId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleProcessAgentRequest(req.id, 'reject')}
                                    disabled={processingRequestId === req.id}
                                    className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                              <p className="text-xs text-gray-500 text-right">
                                {new Date(req.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* KPI Dashboard Tab */}
            <TabsContent value="kpi-dashboard">
              <AdminKPIOverview />
            </TabsContent>

            {/* Journey Analytics Tab */}
            <TabsContent value="journey-analytics">
              <JourneyAnalytics onStageClick={handleJourneyStageClick} />
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights">
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Network Insights & Announcements</h3>
                      <p className="text-gray-500 text-sm">Share updates with students, agents, and employees</p>
                    </div>
                    <Dialog open={showAddInsight} onOpenChange={setShowAddInsight}>
                      <DialogTrigger asChild>
                        <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="add-insight-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Insight
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="text-gray-800 dark:text-white">Create New Insight</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateInsight} className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-gray-600 dark:text-gray-300">Title *</Label>
                            <Input
                              value={newInsight.title}
                              onChange={(e) => setNewInsight({ ...newInsight, title: e.target.value })}
                              className="bg-[#e9ecef] border-gray-200"
                              placeholder="e.g., New University Partnership"
                              required
                              data-testid="insight-title"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-600 dark:text-gray-300">Content *</Label>
                            <Textarea
                              value={newInsight.content}
                              onChange={(e) => setNewInsight({ ...newInsight, content: e.target.value })}
                              className="bg-[#e9ecef] border-gray-200 min-h-[100px]"
                              placeholder="Write your announcement..."
                              required
                              data-testid="insight-content"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-600 dark:text-gray-300">Target Audience</Label>
                              <Select 
                                value={newInsight.target_audience}
                                onValueChange={(val) => setNewInsight({ ...newInsight, target_audience: val })}
                              >
                                <SelectTrigger className="bg-[#e9ecef] border-gray-200" data-testid="insight-audience">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#e9ecef] border-gray-200">
                                  <SelectItem value="all">Everyone</SelectItem>
                                  <SelectItem value="students">Students Only</SelectItem>
                                  <SelectItem value="agents">Agents Only</SelectItem>
                                  <SelectItem value="employees">Employees Only</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-600 dark:text-gray-300">Priority</Label>
                              <Select 
                                value={newInsight.priority}
                                onValueChange={(val) => setNewInsight({ ...newInsight, priority: val })}
                              >
                                <SelectTrigger className="bg-[#e9ecef] border-gray-200" data-testid="insight-priority">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#e9ecef] border-gray-200">
                                  <SelectItem value="normal">Normal</SelectItem>
                                  <SelectItem value="important">Important</SelectItem>
                                  <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <input
                              type="checkbox"
                              id="send_email"
                              checked={newInsight.send_email}
                              onChange={(e) => setNewInsight({ ...newInsight, send_email: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              data-testid="insight-send-email"
                            />
                            <Label htmlFor="send_email" className="text-gray-600 dark:text-gray-300 text-sm cursor-pointer flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              Send email notification to {newInsight.target_audience === 'all' ? 'everyone' : newInsight.target_audience}
                            </Label>
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                            disabled={submittingInsight}
                            data-testid="insight-submit"
                          >
                            {submittingInsight ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />}
                            Publish Insight
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {allInsights.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No insights published yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {allInsights.map((insight) => (
                        <div key={insight.id} className={`p-5 rounded-xl ${insight.priority === 'urgent' ? 'bg-red-500/10 border border-red-500/30' : insight.priority === 'important' ? 'bg-teal-500/10 border border-teal-300' : 'bg-[#e9ecef]/50'}`} data-testid={`insight-${insight.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={insight.target_audience === 'students' ? 'bg-blue-100 text-blue-600' : insight.target_audience === 'agents' ? 'bg-purple-100 text-purple-600' : insight.target_audience === 'employees' ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-600'}>
                                  {insight.target_audience === 'all' ? 'Everyone' : insight.target_audience === 'employees' ? 'Employees' : insight.target_audience}
                                </Badge>
                                <Badge className={insight.priority === 'urgent' ? 'bg-red-100 text-red-600' : insight.priority === 'important' ? 'bg-teal-100 text-teal-600' : 'bg-gray-300 text-gray-600'}>
                                  {insight.priority}
                                </Badge>
                                {!insight.is_active && <Badge className="bg-gray-300 text-gray-500 dark:text-gray-400">Inactive</Badge>}
                              </div>
                              <h4 className="text-gray-800 font-medium mb-1">{insight.title}</h4>
                              <p className="text-gray-500 text-sm">{insight.content}</p>
                              <p className="text-gray-500 text-xs mt-2">
                                By {insight.created_by} on {new Date(insight.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteInsight(insight.id)}
                              className="text-red-600 hover:bg-red-500/10"
                              data-testid={`delete-insight-${insight.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Scholarships Tab */}
            <TabsContent value="scholarships">
              <AdminScholarshipManager />
            </TabsContent>


            {/* Attendance Tab */}
            <TabsContent value="attendance">
              <AttendanceTab
                employees={employees}
                todayAttendance={todayAttendance}
                attendanceData={attendanceData}
                attendanceViewMode={attendanceViewMode}
                setAttendanceViewMode={setAttendanceViewMode}
                attendanceDateRange={attendanceDateRange}
                setAttendanceDateRange={setAttendanceDateRange}
                attendanceEmployeeFilter={attendanceEmployeeFilter}
                setAttendanceEmployeeFilter={setAttendanceEmployeeFilter}
                onFetchTodayAttendance={fetchTodayAttendance}
                onFetchAttendanceData={fetchAttendanceData}
                user={user}
              />
            </TabsContent>

            {/* My Attendance Tab for Employees */}
            <TabsContent value="my-attendance">
              <MyAttendanceTab
                officeInfo={officeInfo}
                myAttendanceStatus={myAttendanceStatus}
                myAttendanceHistory={myAttendanceHistory}
                locationError={locationError}
                checkingIn={checkingIn}
                checkingOut={checkingOut}
                onCheckIn={handleEmployeeCheckIn}
                onCheckOut={handleEmployeeCheckOut}
              />
            </TabsContent>

            <TabsContent value="saving-pots">
              <StaffSavingPotManager user={user} />
            </TabsContent>

            {/* System Health Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="system-health">
              <SystemHealthTab preFilterAgentId={new URLSearchParams(location.search).get('recover') || ''} />
            </TabsContent>
            )}

            {/* Marketing Kit Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="marketing-kit">
              <MarketingKit />
            </TabsContent>
            )}

            {/* Visa Outcomes Tab — AI predictor calibration */}
            <TabsContent value="visa-outcomes">
              <VisaOutcomesTab />
            </TabsContent>

            {/* Fee Targets Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="fee-targets">
              <StaffTargetsManager user={user} />
            </TabsContent>
            )}

            {/* Leave Management Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="leave-management">
              <AdminLeaveManagement />
            </TabsContent>
            )}

            {/* HR Management Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="hr-management">
              <HRManagement />
            </TabsContent>
            )}

            {/* Task Hub Tab for Super Admins OR employees with task permission */}
            {(user?.is_super_admin || user?.permissions?.can_assign_tasks) && (
            <TabsContent value="task-hub">
              <TaskHub />
            </TabsContent>
            )}

            {/* My Leave Tab for Employees */}
            {user?.employee_id && (
            <TabsContent value="my-leave">
              <LeaveRequestComponent />
            </TabsContent>
            )}

            {/* My HR Portal Tab for Employees */}
            {user?.employee_id && (
            <TabsContent value="my-hr">
              <EmployeeHRPortal />
            </TabsContent>
            )}

            {/* Claude AI Studio — all staff */}
            <TabsContent value="ai-studio">
              <AIStudio />
            </TabsContent>

            {/* Database Tools Tab for Super Admins */}
            {user?.is_super_admin && (
            <TabsContent value="db-tools">
              <DatabaseToolsSection />
            </TabsContent>
            )}
          </Tabs>
        </div>
        </div>
      </main>

      {/* Application Details Dialog */}
      <Dialog open={showAppDetails} onOpenChange={setShowAppDetails}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Application Details</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-6 mt-4">
              {/* Student Info */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3">Student Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Name</p>
                    <p className="text-gray-800 dark:text-white">{selectedApp.student_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Email</p>
                    <p className="text-gray-800 dark:text-white">{selectedApp.student_email || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Course Info */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3">Course Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Course</span>
                    <span className="text-teal-600 font-medium">{selectedApp.course_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">University</span>
                    <span className="text-gray-800 dark:text-white">{selectedApp.university_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Country</span>
                    <span className="text-gray-800 dark:text-white">{selectedApp.country}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Applied On</span>
                    <span className="text-gray-800 dark:text-white">{new Date(selectedApp.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3">Uploaded Documents ({selectedApp.documents?.length || 0})</h4>
                {selectedApp.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedApp.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-100 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-teal-600" />
                          <span className="text-gray-800 text-sm">{doc.filename}</span>
                        </div>
                        <Badge className="text-xs">{doc.type?.replace('_', ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No documents uploaded</p>
                )}
              </div>

              {/* Current Status */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3">Current Status</h4>
                <Badge className={`${getStatusColor(selectedApp.status)} text-sm`}>
                  {selectedApp.status?.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>

              {/* Communication Thread */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-teal-600" />
                  Communication Thread
                </h4>
                
                {/* Notes List */}
                <div className="max-h-60 overflow-y-auto space-y-3 mb-4">
                  {loadingAppNotes ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
                    </div>
                  ) : appNotes.length > 0 ? (
                    appNotes.map((note) => (
                      <div 
                        key={note.id} 
                        className={`p-3 rounded-lg ${
                          note.is_internal 
                            ? 'bg-teal-500/10 border border-yellow-500/30' 
                            : note.author_role === 'admin' 
                              ? 'bg-blue-500/10 border border-blue-500/30'
                              : 'bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800 dark:text-white">{note.author_name}</span>
                            {note.is_internal && (
                              <Badge className="bg-teal-100 text-teal-600 text-xs">Internal</Badge>
                            )}
                            <Badge className={`text-xs ${note.author_role === 'admin' ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                              {note.author_role}
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">{note.content}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No messages yet</p>
                  )}
                </div>

                {/* Add Note Form */}
                <div className="space-y-3 pt-3 border-t border-gray-200">
                  <Textarea
                    value={newAppNote}
                    onChange={(e) => setNewAppNote(e.target.value)}
                    placeholder="Type a message to the student or add an internal note..."
                    className="bg-white border-gray-200 text-gray-800 text-sm"
                    rows={2}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="internal-note" 
                        checked={isInternalNote}
                        onCheckedChange={setIsInternalNote}
                      />
                      <label htmlFor="internal-note" className="text-sm text-gray-500 cursor-pointer">
                        Internal note (not visible to student)
                      </label>
                    </div>
                    <Button
                      onClick={handleAddAppNote}
                      disabled={!newAppNote.trim() || sendingAppNote}
                      size="sm"
                      className="bg-teal-600 text-white hover:bg-teal-700"
                    >
                      {sendingAppNote ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          {isInternalNote ? 'Add Note' : 'Send'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-3">
                <h4 className="text-gray-800 font-medium">Quick Actions</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleExportPDF(selectedApp)}
                    variant="outline"
                    className="border-emerald-400/50 text-emerald-600 hover:bg-emerald-400/10"
                    data-testid="action-export-pdf"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'request_docs')}
                    variant="outline"
                    className="border-teal-400 text-teal-600 hover:bg-teal-600/10"
                    data-testid="action-request-docs"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Request Documents
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'pass_counselor')}
                    variant="outline"
                    className="border-blue-400/50 text-blue-600 hover:bg-blue-400/10"
                    data-testid="action-pass-counselor"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Pass to Counselor
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'pass_application_team')}
                    variant="outline"
                    className="border-purple-400/50 text-purple-600 hover:bg-purple-400/10"
                    data-testid="action-pass-app-team"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Pass to App Team
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'accept')}
                    className="bg-green-600 hover:bg-green-500 text-white"
                    data-testid="action-accept"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'reject')}
                    variant="outline"
                    className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                    data-testid="action-reject"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleQuickAction(selectedApp.id, 'enroll')}
                    className="bg-purple-600 hover:bg-purple-500 text-gray-800 dark:text-white"
                    data-testid="action-enroll"
                  >
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Enroll
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showCSVImport} onOpenChange={setShowCSVImport}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Bulk CSV Import</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <CSVImport defaultMode={csvImportMode} onImportComplete={handleCSVImportComplete} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Review Dialog */}
      <Dialog open={showAgentDetails} onOpenChange={setShowAgentDetails}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Agent Review</DialogTitle>
          </DialogHeader>
          {selectedAgent && (
            <div className="space-y-6 mt-4">
              {/* Agent Info */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-teal-600" />
                  Agent Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Full Name</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Email</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Phone</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Experience</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.years_experience} years</p>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4 text-blue-600" />
                  Company Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Company Name</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.company_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Location</p>
                    <p className="text-gray-800 dark:text-white">{selectedAgent.city}, {selectedAgent.country}</p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  Certifications
                </h4>
                <div className="flex items-center gap-3">
                  <Badge className={selectedAgent.british_council_certified ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}>
                    British Council: {selectedAgent.british_council_certified ? 'Certified' : 'Not Certified'}
                  </Badge>
                </div>
              </div>

              {/* Current Status */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3">Current Status</h4>
                <Badge className={`${getAgentStatusColor(selectedAgent.approval_status || 'pending')} text-sm`}>
                  {(selectedAgent.approval_status || 'pending').replace('_', ' ').toUpperCase()}
                </Badge>
                {selectedAgent.rejection_reason && (
                  <p className="text-red-600 text-sm mt-2">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Rejection Reason: {selectedAgent.rejection_reason}
                  </p>
                )}
              </div>

              {/* Rejection Reason Input (for rejecting) */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <Label className="text-gray-600 mb-2 block">Rejection Reason (required if rejecting)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="bg-[#f1f3f5] border-gray-200 text-gray-800 dark:text-white"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <h4 className="text-gray-800 font-medium">Actions</h4>
                <p className="text-gray-500 text-xs mb-3">Actions will send an email notification to the agent</p>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={() => handleAgentStatusUpdate('approved')}
                    className="bg-green-600 hover:bg-green-500 text-white"
                    data-testid="agent-approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleAgentStatusUpdate('compliance_review')}
                    className="bg-orange-600 hover:bg-orange-500 text-gray-800 dark:text-white"
                    data-testid="agent-compliance-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Compliance Check
                  </Button>
                  <Button
                    onClick={() => handleAgentStatusUpdate('rejected')}
                    variant="outline"
                    className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                    data-testid="agent-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student Notes Dialog */}
      <Dialog open={showStudentDetails} onOpenChange={setShowStudentDetails}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Student Notes & Actions</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-6 mt-4">
              {/* Student Info */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  Student Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Name</p>
                    <p className="text-gray-800 dark:text-white">{selectedStudent.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Email</p>
                    <p className="text-gray-800 dark:text-white">{selectedStudent.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Phone</p>
                    <p className="text-gray-800 dark:text-white">{selectedStudent.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Joined</p>
                    <p className="text-gray-800 dark:text-white">{new Date(selectedStudent.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              {selectedStudent.profile && (
                <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                  <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-teal-600" />
                    Profile Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Preferred Country</p>
                      <p className="text-gray-800 dark:text-white">{selectedStudent.profile.preferred_country || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Field of Study</p>
                      <p className="text-gray-800 dark:text-white">{selectedStudent.profile.field_of_study || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Education Level</p>
                      <p className="text-gray-800 capitalize">{selectedStudent.profile.education_level || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Budget</p>
                      <p className="text-gray-800 dark:text-white">{selectedStudent.profile.budget ? `£${selectedStudent.profile.budget}` : 'Not set'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Finance Tab (Direct Students Only) */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <StudentFinanceTab 
                  student={selectedStudent} 
                  user={user}
                  onUpdate={fetchData}
                />
              </div>

              {/* Unified Activity Feed */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <ActivityFeedTimeline entityType="student" entityId={selectedStudent.id} maxHeight="22rem" />
              </div>

              {/* Add Note Section */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-purple-600" />
                  Add Note
                </h4>
                <div className="space-y-3">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter note for student or internal team..."
                    className="bg-[#f1f3f5] border-gray-200 text-gray-800 dark:text-white"
                    rows={3}
                  />
                  <div className="flex items-center justify-between">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-48 bg-[#f1f3f5] border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f1f3f5] border-gray-200">
                        <SelectItem value="internal">
                          <span className="flex items-center gap-2">
                            <Shield className="w-3 h-3 text-orange-600" />
                            Internal Only
                          </span>
                        </SelectItem>
                        <SelectItem value="student_visible">
                          <span className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-green-600" />
                            Send to Student
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim()}
                      className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                      data-testid="add-note-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {noteType === 'student_visible' ? 'Send Note' : 'Add Note'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Existing Notes */}
              <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                <h4 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  Notes History ({studentNotes.length})
                </h4>
                {loadingNotes ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
                  </div>
                ) : studentNotes.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {studentNotes.map((note) => (
                      <div key={note.id} className="p-3 bg-white rounded-lg border border-white/5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={note.note_type === 'internal' ? 'bg-orange-100 text-orange-600 text-xs' : 'bg-green-100 text-green-600 text-xs'}>
                                {note.note_type === 'internal' ? 'Internal' : 'Sent to Student'}
                              </Badge>
                              <span className="text-gray-500 text-xs">{new Date(note.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-gray-600 text-sm">{note.note}</p>
                            <p className="text-gray-500 text-xs mt-1">By: {note.created_by_name}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-600 hover:bg-red-500/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No notes yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Email Preview</DialogTitle>
          </DialogHeader>
          <EmailTemplatePreview
            type={emailPreviewType}
            data={emailPreviewData}
            onClose={handleEmailPreviewClose}
            onConfirm={handleEmailPreviewConfirm}
          />
        </DialogContent>
      </Dialog>

      {/* Employee Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">
              Manage Permissions - {selectedEmployee?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-500 text-sm">
              Configure granular access permissions for this employee.
            </p>

            {/* Applications Section */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-teal-50 px-4 py-3 border-b border-gray-200">
                <p className="text-teal-800 font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Applications
                </p>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_view: c}))} />
                  View Applications
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_create} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_create: c}))} />
                  Create Applications
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_edit} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_edit: c}))} />
                  Edit Applications
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_delete} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_delete: c}))} />
                  Delete Applications
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_change_status} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_change_status: c}))} />
                  Change Status
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.applications_assign} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, applications_assign: c}))} />
                  Assign Applications
                </label>
              </div>
            </div>

            {/* Students Section */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                <p className="text-blue-800 font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Students
                </p>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.students_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, students_view: c}))} />
                  View Students
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.students_create} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, students_create: c}))} />
                  Create Students
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.students_edit} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, students_edit: c}))} />
                  Edit Students
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.students_delete} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, students_delete: c}))} />
                  Delete Students
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.students_add_notes} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, students_add_notes: c}))} />
                  Add Notes
                </label>
              </div>
            </div>

            {/* Leads Section */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-green-50 px-4 py-3 border-b border-gray-200">
                <p className="text-green-800 font-semibold flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Leads
                </p>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_view: c}))} />
                  View Leads
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_create} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_create: c}))} />
                  Create Leads
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_edit} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_edit: c}))} />
                  Edit Leads
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_delete} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_delete: c}))} />
                  Delete Leads
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_assign} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_assign: c}))} />
                  Assign Leads
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.leads_convert} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, leads_convert: c}))} />
                  Convert to Student
                </label>
              </div>
            </div>

            {/* Agents Section */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-purple-50 px-4 py-3 border-b border-gray-200">
                <p className="text-purple-800 font-semibold flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Agents
                </p>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.agents_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, agents_view: c}))} />
                  View Agents
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.agents_approve} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, agents_approve: c}))} />
                  Approve Agents
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.agents_reject} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, agents_reject: c}))} />
                  Reject Agents
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.agents_assign_poc} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, agents_assign_poc: c}))} />
                  Assign POC
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={employeePermissions.agents_view_documents} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, agents_view_documents: c}))} />
                  View Documents
                </label>
              </div>
            </div>

            {/* Courses & Universities Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-orange-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-orange-800 font-semibold flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Courses
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.courses_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, courses_view: c}))} />
                    View Courses
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.courses_add} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, courses_add: c}))} />
                    Add Courses
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.courses_edit} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, courses_edit: c}))} />
                    Edit Courses
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.courses_delete} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, courses_delete: c}))} />
                    Delete Courses
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-indigo-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-indigo-800 font-semibold flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Universities
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.universities_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, universities_view: c}))} />
                    View Universities
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.universities_add} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, universities_add: c}))} />
                    Add Universities
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.universities_edit} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, universities_edit: c}))} />
                    Edit Universities
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.universities_delete} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, universities_delete: c}))} />
                    Delete Universities
                  </label>
                </div>
              </div>
            </div>

            {/* Payouts & Analytics Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-emerald-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-emerald-800 font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Payouts
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.payouts_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, payouts_view: c}))} />
                    View Payouts
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.payouts_process} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, payouts_process: c}))} />
                    Process Payouts
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.payouts_approve} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, payouts_approve: c}))} />
                    Approve Payouts
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-cyan-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-cyan-800 font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Analytics
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.analytics_view_dashboard} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, analytics_view_dashboard: c}))} />
                    View Dashboard
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.analytics_export} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, analytics_export: c}))} />
                    Export Reports
                  </label>
                </div>
              </div>
            </div>

            {/* HR & Tasks Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-rose-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-rose-800 font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    HR Module
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.hr_view_employees} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, hr_view_employees: c}))} />
                    View Employees
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.hr_manage_attendance} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, hr_manage_attendance: c}))} />
                    Manage Attendance
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.hr_manage_leave} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, hr_manage_leave: c}))} />
                    Manage Leave
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-amber-50 px-4 py-3 border-b border-gray-200">
                  <p className="text-amber-800 font-semibold flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Tasks
                  </p>
                </div>
                <div className="p-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.tasks_view} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, tasks_view: c}))} />
                    View Tasks
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.tasks_create} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, tasks_create: c}))} />
                    Create Tasks
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <Checkbox checked={employeePermissions.tasks_assign} onCheckedChange={(c) => setEmployeePermissions(p => ({...p, tasks_assign: c}))} />
                    Assign Tasks
                  </label>
                </div>
              </div>
            </div>

            {/* Transfer Responsibilities Section */}
            <div className="bg-white rounded-lg border-2 border-red-200 overflow-hidden">
              <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                <p className="text-red-800 font-semibold flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4" />
                  Transfer Responsibilities & Deactivate
                </p>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Transfer all assigned leads, tasks, students, applications, and agent POC assignments to another employee. The departing employee&apos;s account will be deactivated after transfer.
                </p>
                <Button
                  variant="outline"
                  onClick={handleOpenTransferDialog}
                  className="border-red-300 text-red-700 hover:bg-red-50 w-full"
                  data-testid="open-transfer-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer Responsibilities
                </Button>
              </div>
            </div>

            <Button 
              onClick={handleSavePermissions}
              disabled={savingPermissions}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold"
              data-testid="save-permissions-btn"
            >
              {savingPermissions ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Permissions
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Responsibilities Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-red-600" />
              Transfer Responsibilities
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                This action will transfer all responsibilities from <strong>{selectedEmployee?.full_name}</strong> to the selected employee and deactivate their account. This cannot be undone.
              </p>
            </div>

            {loadingTransferSummary ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">Loading summary...</p>
              </div>
            ) : transferSummary && (
              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-2">
                <p className="font-medium text-gray-800 text-sm mb-3">Items to transfer:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between bg-gray-50 rounded px-3 py-2">
                    <span className="text-gray-600">Leads</span>
                    <span className="font-semibold text-gray-800" data-testid="transfer-leads-count">{transferSummary.leads}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 rounded px-3 py-2">
                    <span className="text-gray-600">Tasks</span>
                    <span className="font-semibold text-gray-800" data-testid="transfer-tasks-count">{transferSummary.tasks}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 rounded px-3 py-2">
                    <span className="text-gray-600">Students</span>
                    <span className="font-semibold text-gray-800" data-testid="transfer-students-count">{transferSummary.students}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 rounded px-3 py-2">
                    <span className="text-gray-600">Applications</span>
                    <span className="font-semibold text-gray-800" data-testid="transfer-apps-count">{transferSummary.applications}</span>
                  </div>
                  <div className="flex justify-between bg-gray-50 rounded px-3 py-2 col-span-2">
                    <span className="text-gray-600">Agent POC Assignments</span>
                    <span className="font-semibold text-gray-800" data-testid="transfer-poc-count">{transferSummary.agent_poc}</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-gray-700 mb-1 block text-sm">Transfer to:</Label>
              <select
                value={transferTargetId}
                onChange={(e) => setTransferTargetId(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="transfer-target-select"
              >
                <option value="">Select employee...</option>
                {employees
                  .filter(e => e.id !== selectedEmployee?.id && e.is_active !== false)
                  .map(e => (
                    <option key={e.id} value={e.id}>{e.full_name} — {e.position || e.department || 'Staff'}</option>
                  ))
                }
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowTransferDialog(false)}
                className="flex-1"
                data-testid="cancel-transfer-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExecuteTransfer}
                disabled={!transferTargetId || executingTransfer}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-transfer-btn"
              >
                {executingTransfer ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transferring...</>
                ) : (
                  <><ArrowRightLeft className="w-4 h-4 mr-2" />Confirm Transfer</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                placeholder="Enter current password"
                className="mt-1 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                placeholder="Enter new password (min 8 characters)"
                className="mt-1 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                placeholder="Confirm new password"
                className="mt-1 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-teal-600 text-white hover:bg-teal-700"
                data-testid="save-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Change Password'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Assign New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Task Title</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="Enter task title"
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Describe the task..."
                rows={3}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Assign To</Label>
              <Select value={newTask.assigned_to_id} onValueChange={(v) => setNewTask({...newTask, assigned_to_id: v})}>
                <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.user_id || emp.id} className="text-gray-800 dark:text-white">
                      {emp.full_name} ({emp.position || emp.department || 'Employee'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    <SelectItem value="low" className="text-gray-800 dark:text-white">Low</SelectItem>
                    <SelectItem value="medium" className="text-gray-800 dark:text-white">Medium</SelectItem>
                    <SelectItem value="high" className="text-gray-800 dark:text-white">High</SelectItem>
                    <SelectItem value="urgent" className="text-gray-800 dark:text-white">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Due Date</Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
                />
              </div>
            </div>
            <Button
              onClick={async () => {
                if (!newTask.title || !newTask.assigned_to_id) {
                  toast.error('Please fill in title and assignee');
                  return;
                }
                setSubmittingTask(true);
                try {
                  await tasksAPI.create(newTask);
                  toast.success('Task assigned successfully');
                  setShowAddTask(false);
                  setNewTask({ title: '', description: '', assigned_to_id: '', priority: 'medium', due_date: '', category: 'general' });
                  fetchTasks();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to create task');
                } finally {
                  setSubmittingTask(false);
                }
              }}
              disabled={submittingTask}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {submittingTask ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Assign Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={showSendNotification} onOpenChange={setShowSendNotification}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Send Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Title</Label>
              <Input
                value={notificationForm.title}
                onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                placeholder="Notification title"
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Message</Label>
              <Textarea
                value={notificationForm.message}
                onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                placeholder="Your message..."
                rows={3}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Send To</Label>
              <Select value={notificationForm.target_type} onValueChange={(v) => setNotificationForm({...notificationForm, target_type: v})}>
                <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                  <SelectItem value="all" className="text-gray-800 dark:text-white">All Users</SelectItem>
                  <SelectItem value="role" className="text-gray-800 dark:text-white">By Role</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {notificationForm.target_type === 'role' && (
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Target Roles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['student', 'agent', 'admin', 'employee'].map((role) => (
                    <label key={role} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={notificationForm.target_roles.includes(role)}
                        onCheckedChange={(checked) => {
                          const roles = checked 
                            ? [...notificationForm.target_roles, role]
                            : notificationForm.target_roles.filter(r => r !== role);
                          setNotificationForm({...notificationForm, target_roles: roles});
                        }}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{role}s</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={notificationForm.send_email}
                onCheckedChange={(checked) => setNotificationForm({...notificationForm, send_email: checked})}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Also send email notification</span>
            </label>
            <Button
              onClick={async () => {
                if (!notificationForm.title || !notificationForm.message) {
                  toast.error('Please fill in title and message');
                  return;
                }
                setSendingNotification(true);
                try {
                  const res = await notificationsAPI.send(notificationForm);
                  toast.success(`Notification sent to ${res.data.notifications_created} users`);
                  setShowSendNotification(false);
                  setNotificationForm({ title: '', message: '', target_type: 'all', target_roles: [], send_email: false });
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to send notification');
                } finally {
                  setSendingNotification(false);
                }
              }}
              disabled={sendingNotification}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {sendingNotification ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Notification
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset User Password Dialog */}
      <Dialog open={showResetUserPassword} onOpenChange={setShowResetUserPassword}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Reset User Password</DialogTitle>
          </DialogHeader>
          {resetPasswordTarget && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Resetting password for:</p>
                <p className="font-medium text-gray-800 dark:text-white">{resetPasswordTarget.full_name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{resetPasswordTarget.email}</p>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">New Password</Label>
                <Input
                  type="text"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={sendPasswordEmail}
                  onCheckedChange={(checked) => setSendPasswordEmail(checked)}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Send new password via email</span>
              </label>
              <Button
                onClick={async () => {
                  if (!newUserPassword || newUserPassword.length < 6) {
                    toast.error('Password must be at least 6 characters');
                    return;
                  }
                  setResettingPassword(true);
                  try {
                    await adminAPI.resetUserPassword(resetPasswordTarget.id, newUserPassword, sendPasswordEmail);
                    toast.success('Password reset successfully');
                    setShowResetUserPassword(false);
                    setResetPasswordTarget(null);
                    setNewUserPassword('');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to reset password');
                  } finally {
                    setResettingPassword(false);
                  }
                }}
                disabled={resettingPassword}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {resettingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                Reset Password
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
